/**
 * projectService.js — Projects Modülü Servis Dosyası
 *
 * Base URL: https://gw.sakagrup.com:8443/api/v1/projects
 * Auth: getAuthHeader() — Basic Auth + X-user-Id + X-token (teyit edildi)
 *
 * KURAL: uploadProjectFile FormData kullanır, diğerleri JSON.
 * KURAL: standTypeId/standHeightId değişiminden sonra UI getProjectDetail ile reload eder.
 * KURAL: saveProjectSustainability öncesi answers temizlenir (sadece ilgili type field).
 * KURAL: getProjectByStandId'ye standId'yi HER ZAMAN çözülmüş bir stand nesnesinden ver,
 *   route param'ından değil — standOverviewService.js'in :standId route param'ı aslında
 *   mongo _id'dir, standId numeric alanı değil (bkz. FirmaStandDetailPage.jsx).
 */

import axios from 'axios';
import { JAVA_BACKEND_API_URL, getAuthHeader } from '../Components/Contexts/BackendConstants';
import i18n from "../i18n";
import { KOSE_SUB_IDS, KUSBAKISI_SUB_ID } from '../Components/Projects/taahhutnameYardimci';

const BASE = `${JAVA_BACKEND_API_URL}/projects`;

// ─── MOCK MODU ────────────────────────────────────────────────────────────────
export const USE_MOCK_DATA = true; // Backend hazır olunca false yap

const mockDelay = (ms = 150) => new Promise(r => setTimeout(r, ms));

const _mockStandFiles = [
  { stand_file_id: 1, stand_file_name: 'Malzeme Listesi',   stand_file_shortname: 'MAT', stand_file_order: 1 },
  { stand_file_id: 2, stand_file_name: 'Statik Hesap',       stand_file_shortname: 'STA', stand_file_order: 2 },
  { stand_file_id: 3, stand_file_name: 'Elektrik Projesi',   stand_file_shortname: 'ELE', stand_file_order: 3 },
  { stand_file_id: 4, stand_file_name: 'Yangın Önlemleri',   stand_file_shortname: 'YAN', stand_file_order: 4 },
  { stand_file_id: 5, stand_file_name: '3D Görsel',          stand_file_shortname: '3DG', stand_file_order: 5 },
];

const _mockStandTypes = [
  { stand_type_id: 1, stand_type_name: 'Özel Stand',  parent_id: null, file_is_needed: 1 },
  { stand_type_id: 2, stand_type_name: 'Sistem Stand', parent_id: null, file_is_needed: 0 },
  { stand_type_id: 5, stand_type_name: 'Ahşap',        parent_id: 1,    file_is_needed: 1 },
  { stand_type_id: 6, stand_type_name: 'Metal',         parent_id: 1,    file_is_needed: 1 },
  { stand_type_id: 7, stand_type_name: 'Karma',         parent_id: 1,    file_is_needed: 1 },
];

const _mockStandHeights = [
  { stand_height_id: 1, stand_height_name: '0-3m',  stand_height_start: 0, stand_height_end: 3 },
  { stand_height_id: 2, stand_height_name: '3-5m',  stand_height_start: 3, stand_height_end: 5 },
  { stand_height_id: 3, stand_height_name: '5m+',   stand_height_start: 5, stand_height_end: 99 },
];

let _mockSystems = [
  {
    project_stand_system_id: 1,
    stand_type_id: 5, stand_type_name: 'Ahşap',
    stand_height_id: 1, stand_height_name: '0-3m', stand_height_start: 0, stand_height_end: 3,
    requiredFiles: [_mockStandFiles[0], _mockStandFiles[2]],
  },
  {
    project_stand_system_id: 2,
    stand_type_id: 5, stand_type_name: 'Ahşap',
    stand_height_id: 2, stand_height_name: '3-5m', stand_height_start: 3, stand_height_end: 5,
    requiredFiles: [_mockStandFiles[0], _mockStandFiles[1], _mockStandFiles[2]],
  },
  {
    project_stand_system_id: 3,
    stand_type_id: 6, stand_type_name: 'Metal',
    stand_height_id: 1, stand_height_name: '0-3m', stand_height_start: 0, stand_height_end: 3,
    requiredFiles: [_mockStandFiles[0], _mockStandFiles[3]],
  },
];

let _mockProjects = [
  {
    // standId 323 / firma 500 = "Test Decorator" roster'ıyla eşleşir (standOverviewService.js)
    // — Firma/Projeler ve Firma/Anasayfa:standId'de köprülenmiş demo verisi görünsün diye.
    id: 'mock-001', standId: 323, standName: 'A-201', salonName: 'Salon 1',
    firmaId: 73, firmaName: 'ÇARŞIBAŞI KOZMETİK TEKSTİL LTD. ŞTİ.',
    dekoratorFirmaId: 500, dekoratorFirmaName: 'Test Decorator',
    standTypeId: 5, standTypeName: 'Ahşap', standHeightId: 1, standHeightName: '0-3m',
    projectHeightNumber: 2.5, statusId: 2, dealStatus: 1, financeApproval: 0,
    financeText: null, serinProje: false, fuarId: 3,
    requiredFileIds: [1, 3],
    fileApprovals: [
      { standFileId: 1, status: 0, approvedBy: null, updatedAt: null },
      { standFileId: 3, status: 1, approvedBy: 'admin', updatedAt: new Date().toISOString() },
    ],
    requiredCount: 2, approvedCount: 1,
    uploadedFiles: [
      { _id: 'file-001', standFileId: 3, originalFileName: 'elektrik-projesi.pdf',
        storedFileName: 'elektrik-projesi.pdf', isChosen: true, uploadedAt: '2026-06-11T10:00:00.000Z' },
    ],
    textContents: [],
    // Seed: açık BELGE revizyonu — Elektrik Projesi (standFileId 3) tekrar istendi.
    // targetKind DOCUMENT: yanıtı bir metin değil, yeni bir yükleme (o da OPEN →
    // ANSWERED taşır, bkz. notifyDecoratorFileUpload).
    revisions: [
      {
        _id: 'rev-001', type: 'visual', targetKind: 'DOCUMENT', openedByRole: 'STAFF',
        fieldKey: null, standFileId: 3, screenshot: null, status: 'open',
        proposed: null, outcome: null, appliedValue: null,
        messages: [
          { _id: 'msg-001', authorRole: 'inspector', authorName: 'admin', kind: 'message', uploadedFileName: null,
            text: 'Elektrik projesi ölçekli değil, lütfen güncel çizimi tekrar yükleyin.', createdAt: '2026-06-12T09:00:00.000Z' },
        ],
        createdAt: '2026-06-12T09:00:00.000Z', createdBy: 'admin',
        updatedAt: '2026-06-12T09:00:00.000Z', resolvedAt: null,
      },
    ],
    sustainability: null,
    createdAt: '2026-06-10T09:00:00.000Z', updatedAt: '2026-06-10T09:00:00.000Z',
  },
  {
    // standId 324 / firma 500 = "Test Decorator" roster'ıyla eşleşir (bkz. mock-001 notu).
    id: 'mock-002', standId: 324, standName: 'B-11', salonName: 'Salon 2',
    firmaId: 74, firmaName: 'ABC TEKSTİL LTD.',
    dekoratorFirmaId: 500, dekoratorFirmaName: 'Test Decorator',
    standTypeId: 6, standTypeName: 'Metal', standHeightId: 2, standHeightName: '3-5m',
    projectHeightNumber: 4.2, statusId: 1, dealStatus: 1, financeApproval: 1,
    financeText: 'Onaylandı.', serinProje: true, fuarId: 3,
    requiredFileIds: [1, 2, 3],
    fileApprovals: [
      { standFileId: 1, status: 1, approvedBy: 'admin', updatedAt: new Date().toISOString() },
      { standFileId: 2, status: 2, approvedBy: 'admin', updatedAt: new Date().toISOString() },
      { standFileId: 3, status: 1, approvedBy: 'admin', updatedAt: new Date().toISOString() },
    ],
    requiredCount: 3, approvedCount: 2,
    uploadedFiles: [],
    textContents: [{ standFileId: 1, title: 'Malzeme Detayı', text: 'Alüminyum çerçeve, temperli cam paneller.' }],
    // Seed: iki yönün ikisi de — (a) BİZİM açtığımız, tamponu boş bekleyen bir beyan
    // alanı revizyonu (OPEN, sıra onlarda) ve (b) ONLARIN açtığı, değeri hazır gelen
    // bir değişiklik talebi (ANSWERED, sıra bizde). İkisi bir arada olduğu için bu
    // projede inRevision ve pendingProposal AYNI ANDA true — iki ayrı rozet.
    revisions: [
      {
        _id: 'rev-003', type: 'structural', targetKind: 'SPEC_FIELD', openedByRole: 'COMPANY',
        fieldKey: 'declaredAreaM2', standFileId: null, screenshot: null, status: 'answered',
        proposed: { value: 42.5, uploadedFileId: null, note: 'Komşu stand ile sınır düzeltildi.', proposedBy: null, proposedAt: '2026-06-18T08:20:00.000Z' },
        outcome: null, appliedValue: null,
        messages: [
          { _id: 'msg-003', authorRole: 'company', authorName: 'Test Decorator', kind: 'message', uploadedFileName: null,
            text: 'Komşu stand ile sınır düzeltildi.', createdAt: '2026-06-18T08:20:00.000Z' },
        ],
        createdAt: '2026-06-18T08:20:00.000Z', createdBy: 'Test Decorator',
        updatedAt: '2026-06-18T08:20:00.000Z', resolvedAt: null,
      },
      {
        _id: 'rev-002', type: 'structural', targetKind: 'SPEC_FIELD', openedByRole: 'STAFF',
        fieldKey: 'declaredHeightNumber', standFileId: null, screenshot: null, status: 'open',
        proposed: null, outcome: null, appliedValue: null,
        messages: [
          { _id: 'msg-002', authorRole: 'inspector', authorName: 'admin', kind: 'message', uploadedFileName: null,
            text: "Stand yüksekliği 3.5m'yi geçemez, beyan edilen yüksekliği düzeltin.", createdAt: '2026-06-16T10:00:00.000Z' },
        ],
        createdAt: '2026-06-16T10:00:00.000Z', createdBy: 'admin',
        updatedAt: '2026-06-16T10:00:00.000Z', resolvedAt: null,
      },
    ],
    sustainability: null,
    createdAt: '2026-06-12T11:30:00.000Z', updatedAt: '2026-06-15T14:00:00.000Z',
  },
  {
    // standId 325 / firma 500 = "Test Decorator" roster'ıyla eşleşir (bkz. mock-001 notu).
    // Kasıtlı olarak açık revizyonu yok — "temiz" durumun (‼ rozetsiz) demo örneği.
    id: 'mock-003', standId: 325, standName: 'C-19', salonName: 'Salon 3',
    firmaId: 75, firmaName: 'DEF MOBİLYA A.Ş.',
    dekoratorFirmaId: 500, dekoratorFirmaName: 'Test Decorator',
    standTypeId: 5, standTypeName: 'Ahşap', standHeightId: 2, standHeightName: '3-5m',
    projectHeightNumber: 3.8, statusId: 3, dealStatus: 3, financeApproval: 2,
    financeText: 'Katılmıyor.', serinProje: false, fuarId: 3,
    requiredFileIds: [1, 2, 3],
    fileApprovals: [],
    requiredCount: 3, approvedCount: 0,
    uploadedFiles: [], textContents: [], revisions: [], sustainability: null,
    createdAt: '2026-06-13T08:00:00.000Z', updatedAt: '2026-06-13T08:00:00.000Z',
  },
];

const _mockSustainabilityDefinition = {
  id: 'mock-def-001',
  name: 'Stand Sürdürülebilirlik Formu',
  version: 1,
  scopeFuarId: null,
  sections: [
    {
      key: 'materials', title: 'Malzeme ve Yapı', order: 1,
      questions: [
        { key: 'pct_wood',      label: 'Stand yapısının yüzde kaçı ahşap?',                  type: 'NUMBER',       unit: '%', order: 1 },
        { key: 'fsc_wood',      label: 'FSC sertifikalı ahşap kullanılıyor mu?',              type: 'BOOLEAN',              order: 2 },
        { key: 'recycled_pct',  label: 'Geri dönüştürülmüş malzeme oranı',                   type: 'NUMBER',       unit: '%', order: 3 },
      ],
    },
    {
      key: 'energy', title: 'Enerji ve Aydınlatma', order: 2,
      questions: [
        { key: 'led_lighting',  label: 'Aydınlatma tamamen LED mi?',                          type: 'BOOLEAN',              order: 1 },
        { key: 'energy_type',   label: 'Kullanılan enerji kaynağı',                           type: 'SINGLE_CHOICE',        order: 2,
          choices: ['Şebeke', 'Yenilenebilir', 'Jeneratör', 'Karma'] },
      ],
    },
    {
      key: 'waste', title: 'Atık Yönetimi', order: 3,
      questions: [
        { key: 'waste_plan',    label: 'Fuar sonrası atık yönetim planı var mı?',             type: 'BOOLEAN',              order: 1 },
        { key: 'waste_types',   label: 'Ayrıştırılan atık türleri',                           type: 'MULTI_CHOICE',         order: 2,
          choices: ['Kağıt/Karton', 'Metal', 'Plastik', 'Cam', 'Elektronik', 'Organik'] },
        { key: 'waste_notes',   label: 'Ek notlar',                                           type: 'TEXT',                 order: 3 },
      ],
    },
  ],
};

let _mockSustainabilityAnswers = {}; // projectId → answers[]

// ─── Mock: Proje Özel İzni (bölüm 12) ─────────────────────────────────────────
// Canlıda tablo BOŞ (2026-08-28 sayımı) — burada tek bir bekleyen talep tutuyoruz ki
// denetmen tepsisi mock modda da boş görünmesin.
let _mockOzelIzinSeq = 1;
const _mockOzelIzinler = [];

function _mockNewOzelIzin(project, note) {
  const now = new Date().toISOString();
  const codes = (project.projeBeyani?.violations || [])
    .filter((v) => v.severity === 'ERROR' && v.permittable !== false)
    .map((v) => v.code);
  return {
    id: _mockOzelIzinSeq++,
    standId: project.standId ?? null,
    fuarId: project.fuarId ?? null,
    projectId: project.id,
    durum: 1, durumName: OZEL_IZIN_DURUM[1].label, pending: true, approved: false,
    ihlalKodlari: codes,
    talepEden: 1, talepEdenAdi: 'Dekoratör (mock)', talepTarihi: now, talepNotu: note,
    kararVeren: null, kararVerenAdi: null, kararTarihi: null, kararNotu: null,
  };
}

// ─── Mock: fuara özel beyan formları (bölüm 14) ───────────────────────────────
let _mockBeyanFormSeq = 1;
const _mockBeyanFormlari = {
  3: [
    {
      id: _mockBeyanFormSeq++, fuarId: 3, formTipi: 1, formTipiAdi: 'Sürdürülebilirlik Formu',
      formRef: '', formAdi: 'Stand Sürdürülebilirlik Formu', formMissing: false,
      zorunlu: true, sira: 1, aktif: true,
      updateDate: '2026-08-28T09:14:02.000Z', updatedBy: 1,
      completionKnown: false, filled: false, filledAt: null, submissionId: null,
    },
  ],
};
// projectId:formId → doldurulma tarihi
const _mockFormDoldurma = {};

function _mockNewBeyanForm(payload) {
  const formAdi = payload.formTipi === 1 ? 'Stand Sürdürülebilirlik Formu'
    : payload.formTipi === 3 ? 'PDF' : 'Serbest Form (mock)';
  return {
    id: _mockBeyanFormSeq++,
    ...payload,
    formTipiAdi: BEYAN_FORM_TIPI[payload.formTipi]?.label ?? null,
    formAdi,
    formUrl: payload.formTipi === 3 ? `/mock/${payload.formRef}` : null,
    formMissing: false, aktif: true,
    updateDate: new Date().toISOString(), updatedBy: 1,
    completionKnown: false, filled: false, filledAt: null, submissionId: null,
  };
}

// ─── Hata parse yardımcısı ────────────────────────────────────────────────────
// Backend 500'ü plain-text döndürür → error.response?.data string olabilir
// ⚠️ Bu controller'ın 400'lerinin çoğu DÜZ METİN, ama fuar kurallarını değerlendiren
// uçlar (/change-requests, /revisions/{id}/proposal, /revisions/{id}/accept) JSON
// döner: { message, violations[] }. Tek mesaj dört ayrı problemi taşıyamaz ve
// dekoratöre bunları tur tur söylemek dört gidiş-geliş demektir — o yüzden violations
// Error nesnesine iliştirilip UI'a taşınır (bkz. FE-Revisions-and-KVKK-update-guide.md
// "Rule violations come back as JSON").
/**
 * ⚠️ /declare'in 400 gövdesi TEK ŞEKİLLİ DEĞİL (bkz. guide Trap 2 + 2026-08-25 canlı
 * gözlem): yapısal problemler DÜZ METİN, kural ihlalleri JSON döner — ve ihlal listesi
 * bazen { message, violations[] } zarfında, bazen ÇIPLAK BİR DİZİ olarak geliyor
 * (/declaration/validate çıplak dizi kullanıyor). Üçünü de burada karşılıyoruz;
 * body'yi tek bir şekle göre okumak, ihlalleri sessizce yutup "bilinmeyen hata"
 * göstermeye yol açardı.
 */
function parseError(error) {
  const data = error.response?.data;
  const violations = Array.isArray(data) ? data : Array.isArray(data?.violations) ? data.violations : null;
  const message =
    typeof data === 'string'
      ? data
      : data?.message
        ?? (violations?.length ? violations[0].message : null)
        ?? error.message
        ?? i18n.t("services:projectService.bilinmeyenHata");
  const err = new Error(message);
  if (violations) err.violations = violations;
  throw err;
}

// ─────────────────────────────────────────────────────────────────────────────
// 1. CONFIG / DROPDOWN (MySQL)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Üst seviye stand tipleri veya bir tipin alt tiplerini döner.
 * Cascade picker'ı adım adım inşa et.
 * GET /stand-types-classic?parentId={id}
 */
export async function getStandTypes(parentId = null, { forceLive = false } = {}) {
  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();
    return parentId
      ? _mockStandTypes.filter(t => t.parent_id === Number(parentId))
      : _mockStandTypes.filter(t => t.parent_id === null);
  }
  try {
    const params = parentId ? { parentId } : {};
    const res = await axios.get(`${BASE}/stand-types-classic`, {
      params,
      headers: getAuthHeader(),
    });
    return res.data;
  } catch (e) { parseError(e); }
}

/**
 * Bir tip için aktif config'i olan yükseklikleri döner.
 * GET /stand-heights-classic?standTypeId={id}
 * ⚠️ Canlı liste TEKRARLI satır verebiliyor (tip 11 → bant 1 iki kez, catalog guide
 *    "stand-heights-classic") — burada stand_height_id'ye göre tekilleştiriliyor.
 * ⚠️ Sadece YAPRAK tipler bant döner; kök/ara tip ve ölü uç tipler (6, 7, 35, 36) `[]`.
 */
export async function getStandHeights(standTypeId, { forceLive = false } = {}) {
  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();
    return _mockStandHeights; // gerçekte standTypeId'ye göre filtre gelir
  }
  try {
    const res = await axios.get(`${BASE}/stand-heights-classic`, {
      params: { standTypeId },
      headers: getAuthHeader(),
    });
    return dedupeStandHeights(res.data);
  } catch (e) { parseError(e); }
}

/** stand_height_id'ye göre tekilleştirir, ilk görüleni tutar (sıra: start ASC). */
export function dedupeStandHeights(list) {
  const seen = new Set();
  return (Array.isArray(list) ? list : []).filter((h) => {
    const id = Number(h?.stand_height_id);
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  });
}

/**
 * Beyan edilen yükseklikten Sistem Ataması (adım 3) için yükseklik bandını SEÇER.
 *
 * ⚠️ BU BİR FE POLİTİKASI — backend'de "yükseklikten bant" diye bir şey YOK. Catalog
 *    guide açıkça "metreden bant türetilemez, kullanıcı seçer" diyor, çünkü bantlar
 *    ÇAKIŞIYOR (canlı tip 11: 1 [0–3,50] · 2 [3,50–5,50] · 9 [3,50–4,50] · 12 [4,50–12]).
 *    #671: assign-system now refuses a band whose range does not contain the declared height
 *    (both ends included); "Değiştir" offers only those, see heightBandChoices below. The pick
 *    here is always one of them.
 *    WAS (until #671): "assign-system beyan yüksekliğini bandın aralığına karşı HİÇ kontrol etmiyor,
 *    yani yanlış bant sessizce kabul edilir — seçim ekranda görünür ve değiştirilebilir kalmalı."
 * Kurallar:
 *  · Aralık yarı açık [start, end) — belge_kural'ın belgelenmiş kuralıyla aynı. Bantlar
 *    için sınır anlamı belgelenmemiş; tek istisna listenin EN ÜST bandı, onun üst sınırı
 *    dahil (yoksa tam tavandaki beyan hiçbir banda düşmezdi).
 *  · Birden fazla bant tutarsa EN DAR aralık (en spesifik), eşitse başlangıcı yüksek olan.
 * Dönüş: { band, matches, reason } — reason: "MATCH" | "NO_HEIGHT" | "NO_BANDS" | "NO_MATCH".
 */
export function pickHeightBand(bands, height) {
  const list = dedupeStandHeights(bands);
  const h = height == null || height === "" ? NaN : Number(height);
  if (!list.length) return { band: null, matches: [], reason: "NO_BANDS" };
  if (!Number.isFinite(h)) return { band: null, matches: [], reason: "NO_HEIGHT" };
  const num = (v) => (v == null || v === "" ? NaN : Number(v));
  const topEnd = Math.max(...list.map((b) => num(b.stand_height_end)).filter(Number.isFinite));
  const matches = list.filter((b) => {
    const start = num(b.stand_height_start);
    const end = num(b.stand_height_end);
    if (!Number.isFinite(start) || !Number.isFinite(end)) return false;
    return h >= start && (h < end || (h === end && end === topEnd));
  });
  if (!matches.length) return { band: null, matches, reason: "NO_MATCH" };
  const width = (b) => num(b.stand_height_end) - num(b.stand_height_start);
  const band = [...matches].sort((a, b) =>
    width(a) - width(b) || num(b.stand_height_start) - num(a.stand_height_start))[0];
  return { band, matches, reason: "MATCH" };
}

/**
 * #671: the bands "Sistemi Onayla" accepts for a declared height, the backend's own rule
 * (ProjectService.bandContains): start <= height <= end, a missing bound open. With no declared
 * height every band of the type is accepted. Wider than pickHeightBand's matches only at a shared
 * edge (3,50 m fits both "3,50 m'den küçük" and "3,50 m - 5,50 m arası").
 */
export function heightBandChoices(bands, height) {
  const list = dedupeStandHeights(bands);
  const h = height == null || height === "" ? NaN : Number(height);
  if (!Number.isFinite(h)) return list;
  const num = (v) => (v == null || v === "" ? NaN : Number(v));
  return list.filter((b) => {
    const start = num(b.stand_height_start);
    const end = num(b.stand_height_end);
    return (!Number.isFinite(start) || h >= start) && (!Number.isFinite(end) || h <= end);
  });
}

/**
 * Proje oluşturulmadan zorunlu belge önizlemesi.
 * GET /required-files-classic?standTypeId={id}&standHeightId={id}
 */
export async function getRequiredFiles(standTypeId, standHeightId) {
  if (USE_MOCK_DATA) {
    await mockDelay();
    const sys = _mockSystems.find(s => s.stand_type_id === Number(standTypeId) && s.stand_height_id === Number(standHeightId));
    return sys ? sys.requiredFiles : [_mockStandFiles[0]];
  }
  try {
    const res = await axios.get(`${BASE}/required-files-classic`, {
      params: { standTypeId, standHeightId },
      headers: getAuthHeader(),
    });
    return res.data;
  } catch (e) { parseError(e); }
}

/**
 * Tüm belge başlıklarını döner (filtre yok).
 * fileApprovals içindeki standFileId → isim çözümlemek için kullan.
 * GET /stand-files-classic
 */
export async function getAllStandFiles({ forceLive = false } = {}) {
  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();
    return _mockStandFiles;
  }
  try {
    const res = await axios.get(`${BASE}/stand-files-classic`, {
      headers: getAuthHeader(),
    });
    return res.data;
  } catch (e) { parseError(e); }
}

/**
 * Kök'ten verilen tipe kadar breadcrumb zinciri.
 * Edit ekranında cascade picker'ı önceden doldurmak için.
 * GET /stand-type-path?standTypeId={id}
 */
export async function getStandTypePath(standTypeId, { forceLive = false } = {}) {
  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();
    const type = _mockStandTypes.find(t => t.stand_type_id === Number(standTypeId));
    if (!type) return [];
    const path = [{ standTypeId: type.stand_type_id, standTypeName: type.stand_type_name, parentId: type.parent_id, level: 2 }];
    if (type.parent_id) {
      const parent = _mockStandTypes.find(t => t.stand_type_id === type.parent_id);
      if (parent) path.unshift({ standTypeId: parent.stand_type_id, standTypeName: parent.stand_type_name, parentId: null, level: 1 });
    }
    return path;
  }
  try {
    const res = await axios.get(`${BASE}/stand-type-path`, {
      params: { standTypeId },
      headers: getAuthHeader(),
    });
    return res.data;
  } catch (e) { parseError(e); }
}

/**
 * Admin config ekranı: her tip+yükseklik combo + bağlı zorunlu dosyalar.
 * GET /project-systems
 */
export async function getProjectSystems() {
  if (USE_MOCK_DATA) {
    await mockDelay();
    return [..._mockSystems];
  }
  try {
    const res = await axios.get(`${BASE}/project-systems`, {
      headers: getAuthHeader(),
    });
    return res.data;
  } catch (e) { parseError(e); }
}

/**
 * Yeni tip+yükseklik combo oluşturur.
 * 409 Conflict → bu combo zaten var.
 * POST /project-systems?standTypeId={id}&standHeightId={id}
 */
export async function createProjectSystem(standTypeId, standHeightId) {
  if (USE_MOCK_DATA) {
    await mockDelay();
    const exists = _mockSystems.find(s => s.stand_type_id === standTypeId && s.stand_height_id === standHeightId);
    if (exists) { const err = new Error('Bu tip+yükseklik kombinasyonu zaten mevcut. (mock 409)'); throw err; }
    const type = _mockStandTypes.find(t => t.stand_type_id === standTypeId);
    const height = _mockStandHeights.find(h => h.stand_height_id === standHeightId);
    const newSys = {
      project_stand_system_id: Date.now(),
      stand_type_id: standTypeId, stand_type_name: type?.stand_type_name ?? '',
      stand_height_id: standHeightId, stand_height_name: height?.stand_height_name ?? '',
      stand_height_start: height?.stand_height_start, stand_height_end: height?.stand_height_end,
      requiredFiles: [],
    };
    _mockSystems.push(newSys);
    return newSys;
  }
  try {
    const res = await axios.post(`${BASE}/project-systems`, null, { params: { standTypeId, standHeightId }, headers: getAuthHeader() });
    return res.data;
  } catch (e) { parseError(e); }
}

/**
 * Soft delete (is_deleted=1).
 * DELETE /project-systems/{pssId}
 */
export async function deleteProjectSystem(pssId) {
  if (USE_MOCK_DATA) {
    await mockDelay();
    _mockSystems = _mockSystems.filter(s => s.project_stand_system_id !== pssId);
    return { success: true };
  }
  try {
    const res = await axios.delete(`${BASE}/project-systems/${pssId}`, {
      headers: getAuthHeader(),
    });
    return res.data;
  } catch (e) { parseError(e); }
}

/**
 * ⚠️ Hard delete — geri alınamaz. UI'da onay zorunlu.
 * DELETE /project-systems/{pssId}/hard
 */
export async function deleteProjectSystemHard(pssId) {
  if (USE_MOCK_DATA) {
    await mockDelay();
    _mockSystems = _mockSystems.filter(s => s.project_stand_system_id !== pssId);
    return { success: true };
  }
  try {
    const res = await axios.delete(`${BASE}/project-systems/${pssId}/hard`, {
      headers: getAuthHeader(),
    });
    return res.data;
  } catch (e) { parseError(e); }
}

/**
 * Bir combo'nun zorunlu dosya listesini komple değiştirir.
 * Body: düz ID dizisi [1, 3, 5]
 * PUT /project-systems/{pssId}/required-files
 */
export async function setRequiredFiles(pssId, fileIds) {
  if (USE_MOCK_DATA) {
    await mockDelay();
    const sys = _mockSystems.find(s => s.project_stand_system_id === pssId);
    if (sys) sys.requiredFiles = fileIds.map(id => _mockStandFiles.find(f => f.stand_file_id === id)).filter(Boolean);
    return { success: true };
  }
  try {
    const res = await axios.put(`${BASE}/project-systems/${pssId}/required-files`, fileIds, { headers: getAuthHeader() });
    return res.data;
  } catch (e) { parseError(e); }
}

/**
 * Tek zorunlu dosya ekle.
 * POST /project-systems/{pssId}/required-files/{standFileId}
 */
export async function addRequiredFile(pssId, standFileId) {
  if (USE_MOCK_DATA) {
    await mockDelay();
    const sys = _mockSystems.find(s => s.project_stand_system_id === pssId);
    if (sys && !sys.requiredFiles.find(f => f.stand_file_id === standFileId)) {
      const file = _mockStandFiles.find(f => f.stand_file_id === standFileId);
      if (file) sys.requiredFiles.push(file);
    }
    return { success: true };
  }
  try {
    const res = await axios.post(`${BASE}/project-systems/${pssId}/required-files/${standFileId}`, null, { headers: getAuthHeader() });
    return res.data;
  } catch (e) { parseError(e); }
}

/**
 * Tek zorunlu dosya çıkar.
 * DELETE /project-systems/{pssId}/required-files/{standFileId}
 */
export async function removeRequiredFile(pssId, standFileId) {
  if (USE_MOCK_DATA) {
    await mockDelay();
    const sys = _mockSystems.find(s => s.project_stand_system_id === pssId);
    if (sys) sys.requiredFiles = sys.requiredFiles.filter(f => f.stand_file_id !== standFileId);
    return { success: true };
  }
  try {
    const res = await axios.delete(`${BASE}/project-systems/${pssId}/required-files/${standFileId}`, { headers: getAuthHeader() });
    return res.data;
  } catch (e) { parseError(e); }
}

// ─────────────────────────────────────────────────────────────────────────────
// 2. LİSTELEME
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Proje listesi — fuarId zorunlu, standTypeId exact match.
 * ⚠️ fileApprovalStatus gönderilecekse standFileId de mutlaka gönderilmeli (400 döner).
 * GET /
 * @param {Object} params - { fuarId, search, statusId, standTypeId, standHeightId, dealStatus, salonId, standFileId, fileApprovalStatus, page, limit }
 */
export async function getProjects(params = {}) {
  if (USE_MOCK_DATA) {
    await mockDelay();
    let records = [..._mockProjects];
    if (params.fuarId) records = records.filter(p => String(p.fuarId) === String(params.fuarId));
    if (params.search) records = records.filter(p =>
      p.standName?.toLowerCase().includes(params.search.toLowerCase()) ||
      p.firmaName?.toLowerCase().includes(params.search.toLowerCase())
    );
    if (params.statusId) records = records.filter(p => String(p.statusId) === String(params.statusId));
    if (params.dealStatus) records = records.filter(p => String(p.dealStatus) === String(params.dealStatus));
    if (params.standTypeId) records = records.filter(p => String(p.standTypeId) === String(params.standTypeId));
    const page = Number(params.page) || 1;
    const limit = Number(params.limit) || 20;
    const total = records.length;
    records = records.slice((page - 1) * limit, page * limit);
    return { total, page, limit, records };
  }
  try {
    const res = await axios.get(`${BASE}`, { params, headers: getAuthHeader() });
    return res.data; // { total, page, limit, records }
  } catch (e) { parseError(e); }
}

/**
 * Proje listesi — standTypeId subtree expand (tüm alt tipleri dahil eder).
 * Cascade picker'dan üst tip ID geliyorsa bunu kullan.
 * GET /list
 */
export async function getProjectsWithSubtree(params = {}) {
  if (USE_MOCK_DATA) { return getProjects(params); }
  try {
    const res = await axios.get(`${BASE}/list`, { params, headers: getAuthHeader() });
    return res.data; // { total, page, limit, records }
  } catch (e) { parseError(e); }
}

// ─────────────────────────────────────────────────────────────────────────────
// 3. TEK PROJE
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Raw Mongo dokümanı — soft-deleted dosyalar dahil.
 * Sadece debug için. Sayfa render için getProjectDetail kullan.
 * GET /{id}
 */
export async function getProject(id) {
  if (USE_MOCK_DATA) {
    await mockDelay();
    const p = _mockProjects.find(p => p.id === id);
    if (!p) throw new Error('Proje bulunamadı');
    return p;
  }
  try {
    const res = await axios.get(`${BASE}/${id}`, { headers: getAuthHeader() });
    return res.data;
  } catch (e) { parseError(e); }
}

// requiredFileIds → requiredFiles zenginleştirmesi getProjectDetail ve getProjectByStandId
// arasında paylaşılır (bkz. bölüm 11).
function _enrichProject(p) {
  const threads = (p.revisions || []).filter(r => Array.isArray(r.messages));
  return {
    ...p,
    requiredFiles: (p.requiredFileIds || []).map(fid => _mockStandFiles.find(f => f.stand_file_id === fid)).filter(Boolean),
    // Canlı dokümanda bu iki bayrak backend'de denormalize tutulur ve her revizyon
    // yazımında yeniden türetilir; mock tarafında da aynı iki soruya aynı cevabı
    // versinler diye burada türetiliyor (bkz. bölüm 9).
    inRevision: threads.some(r => r.status !== 'resolved'),
    pendingProposal: threads.some(r => r.status === 'answered'),
  };
}

/**
 * Zenginleştirilmiş proje detayı.
 * - uploadedFiles, textContents, revisions, sustainability dahil
 * - ID'ler isimlere çözümlendi
 * - Soft-deleted dosyalar filtrelenmiş
 * ⚠️ Sayfalar için her zaman bunu kullan.
 * GET /{id}/detail
 */
export async function getProjectDetail(id) {
  if (USE_MOCK_DATA) {
    await mockDelay();
    const p = _mockProjects.find(p => p.id === id);
    if (!p) throw new Error('Proje bulunamadı');
    return _enrichProject(p);
  }
  try {
    const res = await axios.get(`${BASE}/${id}/detail`, { headers: getAuthHeader() });
    return res.data;
  } catch (e) { parseError(e); }
}

// ─────────────────────────────────────────────────────────────────────────────
// 4. CREATE / UPDATE
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Proje oluştur.
 * Zorunlu: standId, firmaId, fuarId, standTypeId, standHeightId
 * Yan etki: zorunlu dosya listesi otomatik oluşur, dekoratör firma MySQL'e yazılır.
 * 409 Conflict → aynı standId + fuarId için proje zaten var.
 * POST /
 */
export async function createProject(body, { forceLive = false } = {}) {
  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();
    const newP = {
      ...body, id: `mock-${Date.now()}`,
      standName: `Stand-${body.standId}`, salonName: 'Hall 1',
      firmaName: 'Mock Firma', dekoratorFirmaName: null,
      standTypeName: _mockStandTypes.find(t => t.stand_type_id === body.standTypeId)?.stand_type_name ?? '',
      standHeightName: _mockStandHeights.find(h => h.stand_height_id === body.standHeightId)?.stand_height_name ?? '',
      statusId: 2, financeApproval: 0, financeText: null,
      requiredFileIds: [], fileApprovals: [], requiredCount: 0, approvedCount: 0,
      uploadedFiles: [], textContents: [], revisions: [], sustainability: null,
      createdAt: new Date().toISOString(), updatedAt: new Date().toISOString(),
    };
    _mockProjects.push(newP);
    return newP;
  }
  try {
    const res = await axios.post(`${BASE}`, body, { headers: getAuthHeader() });
    return res.data;
  } catch (e) { parseError(e); }
}

/**
 * Proje güncelle (partial — sadece değişen alanları gönder).
 * ⚠️ standTypeId veya standHeightId değiştirilirse backend zorunlu dosya listesini
 *    yeniden hesaplar → UI'da getProjectDetail ile reload zorunlu!
 * #210: forceLive - Stand Detayı'nın "Proje Bilgilerini Düzenle" penceresi canlı projeyi yazar
 * (USE_MOCK_DATA bu dosyada açık; ProjectDetailPage eskisi gibi mock'a gider).
 * PUT /{id}
 */
// WAS: export async function updateProject(id, body) {
export async function updateProject(id, body, { forceLive = false } = {}) {
  // WAS: if (USE_MOCK_DATA) {
  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();
    const idx = _mockProjects.findIndex(p => p.id === id);
    if (idx === -1) throw new Error('Proje bulunamadı');
    _mockProjects[idx] = { ..._mockProjects[idx], ...body, updatedAt: new Date().toISOString() };
    return _mockProjects[idx];
  }
  try {
    const res = await axios.put(`${BASE}/${id}`, body, { headers: getAuthHeader() });
    return res.data;
  } catch (e) { parseError(e); }
}

/**
 * Beyan gövdesini tel formatına çevirir — /declare ve /declaration/validate AYNI
 * gövdeyi alır, o yüzden tek yerde kurulur.
 *
 * ⚠️ declaredStandTypeId JSON NUMBER olmalı: "13" null okunur ve "Stand tipi beyan
 *    edilmelidir." döner. Diğer sayısal alanlar tırnaklı string'i kabul eder — eski
 *    bir asimetri, 2026-08-24'te gelmedi.
 * ⚠️ null ≠ 0 ≠ false. declaredKw null "elektrik istemiyorum", 0 ise sahte bir
 *    "0 kW" rozeti bastırır. declaredGlass/Paint null "hiç sorulmadı" (fuar
 *    camSorulsun/boyaSorulsun kapalı), false "soruldu, hayır dedi".
 *
 * 2026-09-17 (backend #53): cam/boya artık genişletilebilir bir "kullanım kuralları"
 * kataloğunun iki kaydı. Cevaplar `declaredKullanimlar: { KOD: true|false|null }`
 * haritasıyla gider (null yine "sorulmadı"). declaredGlass/declaredPaint uyumluluk
 * için GÖNDERİLMEYE DEVAM ediyor — açıkça verilmemişlerse haritanın CAM/BOYA
 * değerinden türetilir. Harita hiç verilmediyse anahtar da gönderilmez: eski
 * çağıranlar eskisi gibi davransın.
 */
function _boolOrNull(v) {
  return v == null || v === '' ? null : Boolean(v);
}

function _buildDeclareBody(spec) {
  const numOrNull = (v) => (v === '' || v == null ? null : Number(v));
  const kullanimlar = spec.declaredKullanimlar && typeof spec.declaredKullanimlar === 'object'
    ? Object.fromEntries(Object.entries(spec.declaredKullanimlar).map(([k, v]) => [k, _boolOrNull(v)]))
    : null;
  const body = {
    declaredStandTypeId: spec.declaredStandTypeId == null ? null : Number(spec.declaredStandTypeId),
    declaredHeightNumber: numOrNull(spec.declaredHeightNumber),
    declaredAreaM2: numOrNull(spec.declaredAreaM2),
    declaredKw: numOrNull(spec.declaredKw),
    declaredGlass: _boolOrNull(spec.declaredGlass !== undefined ? spec.declaredGlass : kullanimlar?.CAM),
    declaredPaint: _boolOrNull(spec.declaredPaint !== undefined ? spec.declaredPaint : kullanimlar?.BOYA),
  };
  if (kullanimlar) body.declaredKullanimlar = kullanimlar;
  // 2026-08-28 — Proje Özel İzni. YOKLUK = false, o yüzden bayrağı sadece istendiğinde
  // ekliyoruz: mevcut her çağıran eskisi gibi davranmaya devam etsin.
  // ⚠️ Not anahtarı uçtan uca AYNI DEĞİL: burada `ozelIzinNotu`, POST /{id}/ozel-izin
  //    ve /karar'da `note`. Karıştırmak sessizce notu düşürür.
  if (spec.ozelIzinTalep) {
    body.ozelIzinTalep = true;
    const not = spec.ozelIzinNotu == null ? '' : String(spec.ozelIzinNotu).trim();
    body.ozelIzinNotu = not === '' ? null : not.slice(0, OZEL_IZIN_NOT_MAX);
  }
  return body;
}

/**
 * Proje Beyanı adım 2 — dekoratör kendi alt tipini (projenin kök standTypeId'sinin
 * bir alt/torun düğümü) + beyan ettiği YAPI ÖZELLİKLERİNİ (yükseklik, m², kW, cam,
 * boya) bildirir. staff'ın adım 1'de kurduğu projeyi DEĞİŞTİRMEZ, sadece beyanı kaydeder.
 *
 * 2026-08-24 (bkz. docs/guides/FE-fuar-kural-sistemi-guide.md): akış YÖN DEĞİŞTİRDİ.
 * Artık kimse önce bir yükseklik girmiyor — dekoratör beyan eder, backend beyanı
 * FUAR KURAL SETİNE karşı yargılar, staff adım 3'te KABUL eder.
 *   ihlal varsa  → 400, HİÇBİR ŞEY kaydedilmez, violations[] döner
 *   temizse      → 200, kaydedilir, uyarılar (WARNING) yanında gelir
 *
 * ⚠️ İKİ FARKLI 400 GÖVDESİ var (guide Trap 2): yapısal problemler (tip yok,
 *    yükseklik sıfır, negatif kW) DÜZ METİN döner; kural ihlalleri JSON
 *    { message, violations[] } döner. parseError ikisini de karşılıyor — ihlaller
 *    err.violations'a takılır, çağıran KuralIhlalListesi ile basar.
 * ⚠️ Tek bir ihlali değil, HEPSİNİ gösterin — backend kasıtlı olarak tümünü döner.
 * ⚠️ adım 3 (assign-system) ÇALIŞTIKTAN SONRA bu uç 400 döner — "beyan kabul edilmiş,
 *    doğrudan değiştirilemez. Değişiklik talebi açın". UI `projeBeyani.systemAssigned`
 *    true iken "Beyanı Düzenle"yi "Değişiklik Talebi"ne (createChangeRequest) çevirmeli.
 *
 * POST /{id}/declare
 */
export async function declareProject(id, spec, { forceLive = false } = {}) {
  const body = _buildDeclareBody(spec);
  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();
    const p = _mockProjects.find(p => p.id === id);
    if (!p) throw new Error('Proje bulunamadı');
    const now = new Date().toISOString();
    p.projeBeyani = {
      ...(p.projeBeyani || {}),
      declared: true,
      declaredStandTypeId: body.declaredStandTypeId,
      declaredHeightNumber: body.declaredHeightNumber,
      declaredAreaM2: body.declaredAreaM2,
      declaredKw: body.declaredKw,
      declaredGlass: body.declaredGlass,
      declaredPaint: body.declaredPaint,
      // Canlı DTO haritayı CAM/BOYA dahil TAM döner — mock da öyle.
      declaredKullanimlar: {
        ...(body.declaredKullanimlar || {}),
        CAM: body.declaredGlass,
        BOYA: body.declaredPaint,
      },
      declaredAt: now, declaredBy: null,
      systemAssigned: false, systemAssignedAt: null, systemAssignedBy: null,
      awaitingSystemAssignment: true,
      accepted: false, acceptedHeightNumber: null,
      locked: false, lockReason: null, overridden: false, violations: [],
    };
    p.updatedAt = now;
    return p.projeBeyani;
  }
  try {
    const res = await axios.post(`${BASE}/${id}/declare`, body, { headers: getAuthHeader() });
    return res.data;
  } catch (e) { parseError(e); }
}

/**
 * Kuru çalıştırma — /declare ile AYNI gövde, HİÇBİR ŞEY yazmaz, HER ZAMAN 200.
 * Dönen violations[] boşsa beyan kabul edilirdi.
 *
 * Bunun varlık sebebi: dekoratörün bir kuralı "reddedilerek" öğrenmesini engellemek.
 * Form doldurulurken (debounce'lu) çağrılır, ihlaller ilgili inputun altında canlı görünür.
 * POST /{id}/declaration/validate
 */
export async function validateDeclaration(id, spec, { forceLive = false } = {}) {
  const body = _buildDeclareBody(spec);
  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay(80);
    return { violations: [] };
  }
  try {
    const res = await axios.post(`${BASE}/${id}/declaration/validate`, body, { headers: getAuthHeader() });
    // ⚠️ Bu uç ÇIPLAK BİR DİZİ dönüyor (teyit: proje 6a7afd5b…, 2026-08-25), /declare'in
    //    400 gövdesi ise { message, violations[] }. Aynı listeyi iki farklı sarmalayışla
    //    veren iki uç var; çağıranların ikisini de bilmesi gerekmesin diye burada tek
    //    şekle indiriliyor. (`res.data.violations` diye okumak sessizce BOŞ liste verir —
    //    yani "her şey yolunda" gibi görünür, ki en kötü hata biçimi budur.)
    return { violations: Array.isArray(res.data) ? res.data : res.data?.violations ?? [] };
  } catch (e) { parseError(e); }
}



/**
 * Proje Beyanı adım 3 — bu artık literal olarak "BEYAN ETTİĞİNİ KABUL EDİYORUM" yazımı.
 * Staff bir yükseklik bandı (standHeightId) atar ve backend aynı çağrıda:
 *   · declaredHeightNumber → projectHeightNumber kopyalar (KABUL EDİLEN yükseklik)
 *   · cam/boya beyanını stand_detail'e yansıtır
 *   · zorunlu belge listesini 5 SEVİYELİ kaskadla yeniden çözer (bkz. belgeKuralService.js)
 * ⚠️ Mevcut onaylar artık gerekmeyen başlıklar için düşebilir (bkz. guide Trap 7).
 * ⚠️ Yükseklik BANDI hâlâ önemli: belgeler artık ona bağlı değil ama Stand Talep
 *    dosyalama gate'i (isProjectDeclared) standTypeId != null && standHeightId != null
 *    istiyor — bu adım bandı atamaya devam etmeli.
 * standTypeId gönderilmezse backend declaredStandTypeId'yi kullanır.
 * PATCH /{id}/declaration/assign-system
 */
export async function assignProjectSystem(id, standHeightId, { forceLive = false } = {}) {
  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();
    const p = _mockProjects.find(p => p.id === id);
    if (!p) throw new Error('Proje bulunamadı');
    const now = new Date().toISOString();
    const height = _mockStandHeights.find(h => h.stand_height_id === Number(standHeightId));
    const pb = p.projeBeyani || {};
    p.projeBeyani = {
      ...pb,
      standHeightId: Number(standHeightId),
      standHeightName: height?.stand_height_name ?? null,
      systemAssigned: true, systemAssignedAt: now, systemAssignedBy: null,
      awaitingSystemAssignment: false,
      // Kabul yazımı — beyan edilen yükseklik artık "kabul ettiğimiz" yükseklik.
      accepted: true, acceptedHeightNumber: pb.declaredHeightNumber ?? null,
    };
    if (pb.declaredHeightNumber != null) p.projectHeightNumber = pb.declaredHeightNumber;
    p.updatedAt = now;
    return p.projeBeyani;
  }
  try {
    const res = await axios.patch(`${BASE}/${id}/declaration/assign-system`, {
      standHeightId: Number(standHeightId),
    }, { headers: getAuthHeader() });
    return res.data;
  } catch (e) { parseError(e); }
}

/**
 * Kilidi staff elle kaldırır — beyan edilen sayıları DEĞİŞTİRMEZ, sadece "biliyoruz,
 * konuştuk" bayrağını işaretler. Yeni bir beyan (adım 2 tekrar) override'ı siler.
 *
 * ⚠️ 2026-08-24: kilidin ANLAMI değişti. Eski tek sebep `HEIGHT_MISMATCH` (söz verilen
 *    ≠ beyan edilen) EMEKLİYE AYRILDI — eski kurala göre kilitli her proje bu sürümle
 *    açılır, beklenen davranış, hata değil. Kilit artık fuar kural setinden geliyor:
 *    HEIGHT_ABOVE_LIMIT, STAND_TYPE_FORBIDDEN, AREA_BELOW_MINIMUM, ...
 *    `locked` HER OKUMADA yeniden türetilir (saklanmaz) ve ihlal listesinin TAMAMINI
 *    yeniden hesaplar: organizatör tavanı beyan KABUL EDİLDİKTEN SONRA düşürürse
 *    kendiliğinden görünür hale gelir.
 *    `lockReason` sadece İLK ihlalin tek kelimelik özeti — gerçek cevap `violations[]`.
 * PATCH /{id}/height-lock/override
 */
export async function overrideHeightLock(id, note, { forceLive = false } = {}) {
  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();
    const p = _mockProjects.find(p => p.id === id);
    if (!p) throw new Error('Proje bulunamadı');
    const now = new Date().toISOString();
    p.projeBeyani = {
      ...(p.projeBeyani || {}),
      locked: false, overridden: true, overrideBy: null, overrideAt: now,
      overrideNote: note?.trim() || null,
    };
    p.updatedAt = now;
    return p.projeBeyani;
  }
  try {
    const res = await axios.patch(`${BASE}/${id}/height-lock/override`, {
      note: note?.trim() || null,
    }, { headers: getAuthHeader() });
    return res.data;
  } catch (e) { parseError(e); }
}

/**
 * Zorunlu belge listesini TEK bir proje için yeniden çözer.
 * `requiredFileIds` bir anlık görüntüdür — bir belge kuralı (belge_kural) eklemek,
 * düzenlemek ya da silmek, o kurala göre ÇÖZÜLMÜŞ projelere geri dönmez. Değişikliği
 * mevcut projelere itmenin yolu budur (davranış 2026-08-24 öncesiyle aynı).
 * POST /{id}/reresolve
 */
export async function reresolveProject(id, { forceLive = false } = {}) {
  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();
    return { reresolved: 1 };
  }
  try {
    const res = await axios.post(`${BASE}/${id}/reresolve`, {}, { headers: getAuthHeader() });
    return res.data;
  } catch (e) { parseError(e); }
}

/**
 * Aynısı, bir fuarın TÜM projeleri için. Belge Kuralları ekranında bir kural
 * yazıldıktan sonra çağrılacak toplu işlem.
 * POST /fuar/{fuarId}/reresolve
 */
export async function reresolveFuar(fuarId, { forceLive = false } = {}) {
  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay(300);
    return { reresolved: 0 };
  }
  try {
    const res = await axios.post(`${BASE}/fuar/${fuarId}/reresolve`, {}, { headers: getAuthHeader() });
    return res.data;
  } catch (e) { parseError(e); }
}

// ─────────────────────────────────────────────────────────────────────────────
// 5. DOSYA İŞLEMLERİ
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Dosya yükle — multipart/form-data (JSON değil!).
 * ⚠️ FormData manuel oluşturulur, Content-Type açıkça belirtilir.
 * POST /{id}/files
 */
export async function uploadProjectFile(id, standFileId, file, subStandFileId = null) {
  if (USE_MOCK_DATA) {
    await mockDelay(400);
    const p = _mockProjects.find(p => p.id === id);
    if (p) {
      const newFile = {
        _id: `file-${Date.now()}`, standFileId: Number(standFileId),
        originalFileName: file.name, storedFileName: file.name,
        isChosen: false, uploadedAt: new Date().toISOString(),
      };
      p.uploadedFiles = [...(p.uploadedFiles || []), newFile];
    }
    return { success: true };
  }
  try {
    const formData = new FormData();
    formData.append('standFileId', standFileId);
    formData.append('file', file);
    if (subStandFileId) formData.append('subStandFileId', subStandFileId);
    const res = await axios.post(`${BASE}/${id}/files`, formData, {
      headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' },
    });
    return res.data;
  } catch (e) { parseError(e); }
}

/**
 * Dosyayı projeden soft-sil (disk'teki dosya korunur).
 * resetTitleApproval=true → son aktif dosyaysa onay durumu pending'e döner.
 * DELETE /{id}/files/{fileId}?resetTitleApproval=true
 */
export async function deleteProjectFile(id, fileId, resetTitleApproval = false) {
  if (USE_MOCK_DATA) {
    await mockDelay();
    const p = _mockProjects.find(p => p.id === id);
    if (p) p.uploadedFiles = (p.uploadedFiles || []).filter(f => f._id !== fileId);
    return { success: true };
  }
  try {
    const res = await axios.delete(`${BASE}/${id}/files/${fileId}`, { params: { resetTitleApproval }, headers: getAuthHeader() });
    return res.data;
  } catch (e) { parseError(e); }
}

/**
 * Bu dosyayı belge başlığı için "seçili" işaretle ve otomatik onayla.
 * ⚠️ Sonrasında UI'da getProjectDetail ile reload et — ayrıca setFileApproval çağırma.
 * PUT /{id}/files/{fileId}/choose
 */
export async function chooseProjectFile(id, fileId) {
  if (USE_MOCK_DATA) {
    await mockDelay();
    const p = _mockProjects.find(p => p.id === id);
    if (p) {
      const chosenFile = (p.uploadedFiles || []).find(f => f._id === fileId);
      if (chosenFile) {
        (p.uploadedFiles || []).forEach(f => { if (f.standFileId === chosenFile.standFileId) f.isChosen = false; });
        chosenFile.isChosen = true;
        // Otomatik onayla
        const fa = (p.fileApprovals || []).find(f => f.standFileId === chosenFile.standFileId);
        if (fa) fa.status = 1;
        else p.fileApprovals = [...(p.fileApprovals || []), { standFileId: chosenFile.standFileId, status: 1, approvedBy: 'mock', updatedAt: new Date().toISOString() }];
        p.approvedCount = (p.fileApprovals || []).filter(f => f.status === 1).length;
      }
    }
    return { success: true };
  }
  try {
    const res = await axios.put(`${BASE}/${id}/files/${fileId}/choose`, null, { headers: getAuthHeader() });
    return res.data;
  } catch (e) { parseError(e); }
}

/**
 * Belge başlığının onay durumunu direkt set et.
 * status: 0=Beklemede, 1=Onaylandı, 2=Reddedildi
 * PUT /{id}/file-approval?standFileId={id}&status={status}
 */
export async function setFileApproval(id, standFileId, status) {
  if (USE_MOCK_DATA) {
    await mockDelay();
    const p = _mockProjects.find(p => p.id === id);
    if (p) {
      const fa = (p.fileApprovals || []).find(f => f.standFileId === Number(standFileId));
      if (fa) fa.status = Number(status);
      else p.fileApprovals = [...(p.fileApprovals || []), { standFileId: Number(standFileId), status: Number(status), approvedBy: 'mock', updatedAt: new Date().toISOString() }];
      p.approvedCount = (p.fileApprovals || []).filter(f => f.status === 1).length;
    }
    return { success: true };
  }
  try {
    const res = await axios.put(`${BASE}/${id}/file-approval`, null, { params: { standFileId, status }, headers: getAuthHeader() });
    return res.data;
  } catch (e) { parseError(e); }
}

// #217 - the generated taahhutnameler (#216) and the electrician's signature on the Elektrik one (#505). LIVE projects only:
// a mock project has none of these endpoints, so there is no mock branch (the page keeps its old tiles for mock stands).

/**
 * The project's three taahhutnameler as generated documents, with where each stands (state, company approval, signed copy,
 * electrician, trail) - TaahhutnamelerDTO. Staff also get the names of who chose / reviewed / recorded.
 * GET /{id}/taahhutnameler (optional ?languageId=1|2: the wording and the header names in that language, else the request's own - #666's
 * PDFs read each language they are made in; an English request gets the Turkish wording until an English version is published,
 * text.languageId says which came back)
 */
export async function getTaahhutnameler(id, languageId) {
  try {
    const res = await axios.get(`${BASE}/${id}/taahhutnameler`, { headers: getAuthHeader(), ...(languageId ? { params: { languageId } } : {}) });
    return res.data;
  } catch (e) { parseError(e); }
}

/**
 * Staff record that the stand's electrician signed the Elektrik taahhutname on the fairground (multipart): personId (a kisi of
 * the stand's participant or decorator) OR ad (a typed name); imzaTarihi, not, foto optional. 409 while the decorator has not
 * signed or one is recorded already. Answers the refreshed TaahhutnamelerDTO (with `uyarilar`).
 * POST /{id}/taahhutnameler/elektrik/elektrikci
 */
export async function recordElektrikciImzasi(id, { personId, ad, imzaTarihi, not, foto } = {}) {
  const form = new FormData();
  if (personId != null) form.append('personId', personId);
  if (ad) form.append('ad', ad);
  if (imzaTarihi) form.append('imzaTarihi', imzaTarihi);
  if (not) form.append('not', not);
  if (foto) form.append('foto', foto);
  try {
    const res = await axios.post(`${BASE}/${id}/taahhutnameler/elektrik/elektrikci`, form, {
      headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' },
    });
    return res.data;
  } catch (e) { parseError(e); }
}

/**
 * Add or replace the photo of the paper the electrician signed - may come after the fair ends. 409 when none is recorded.
 * POST /{id}/taahhutnameler/elektrik/elektrikci/foto
 */
export async function setElektrikciFotografi(id, foto) {
  const form = new FormData();
  form.append('foto', foto);
  try {
    const res = await axios.post(`${BASE}/${id}/taahhutnameler/elektrik/elektrikci/foto`, form, {
      headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' },
    });
    return res.data;
  } catch (e) { parseError(e); }
}

/**
 * Remove a recorded electrician signature (a wrong person); the reason is optional. 409 when none is recorded.
 * DELETE /{id}/taahhutnameler/elektrik/elektrikci?not=
 */
export async function removeElektrikciImzasi(id, not) {
  try {
    const res = await axios.delete(`${BASE}/${id}/taahhutnameler/elektrik/elektrikci`, {
      params: not ? { not } : undefined,
      headers: getAuthHeader(),
    });
    return res.data;
  } catch (e) { parseError(e); }
}

/**
 * The project counts of one fair (staff, or an organizer with the fair-wide view). #506 reads
 * `elektrikTaahhutnamesi {required, awaitingDecorator, decoratorOnly, decoratorAndElectrician}`: the stands that need the Elektrik
 * taahhutname by how far its signatures are (required = the three others added). Live backend only, no mock branch.
 * GET /stats?fuarId=
 */
export async function getProjectStats(fuarId) {
  try {
    const res = await axios.get(`${BASE}/stats`, { params: { fuarId }, headers: getAuthHeader() });
    return res.data;
  } catch (e) { parseError(e); }
}

// ─────────────────────────────────────────────────────────────────────────────
// 6. TEXT CONTENT
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Serbest metin alanları: Malzeme (standFileId=1) ve Elektrik (standFileId=3).
 * PUT /{id}/text-content?standFileId={id}&title={title}&text={text}
 */
export async function setTextContent(id, standFileId, text, title = null) {
  if (USE_MOCK_DATA) {
    await mockDelay();
    const p = _mockProjects.find(p => p.id === id);
    if (p) {
      const existing = (p.textContents || []).find(t => t.standFileId === Number(standFileId));
      if (existing) { existing.text = text; if (title) existing.title = title; }
      else p.textContents = [...(p.textContents || []), { standFileId: Number(standFileId), title: title ?? '', text }];
    }
    return { success: true };
  }
  try {
    const params = { standFileId, text };
    if (title) params.title = title;
    const res = await axios.put(`${BASE}/${id}/text-content`, null, { params, headers: getAuthHeader() });
    return res.data;
  } catch (e) { parseError(e); }
}

// ─────────────────────────────────────────────────────────────────────────────
// 7. DEAL STATUS
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Sadece deal status'ü değiştirmek için kısa yol.
 * dealStatus: 1=Anlaşma Var, 2=Yok, 3=Katılmıyor
 * PUT /{id}/deal-status?dealStatus={1|2|3}
 */
export async function setDealStatus(id, dealStatus) {
  if (USE_MOCK_DATA) {
    await mockDelay();
    const p = _mockProjects.find(p => p.id === id);
    if (p) p.dealStatus = Number(dealStatus);
    return { success: true };
  }
  try {
    const res = await axios.put(`${BASE}/${id}/deal-status`, null, { params: { dealStatus }, headers: getAuthHeader() });
    return res.data;
  } catch (e) { parseError(e); }
}

// ─────────────────────────────────────────────────────────────────────────────
// 8. SÜRDÜRÜLEBİLİRLİK (Proje Başına)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Aktif sustainability definition'ı çeker.
 * fuarId verilirse o fuara özel, verilmezse global default.
 * GET /sustainability/definition?fuarId={id}
 */
export async function getSustainabilityDefinition(fuarId = null) {
  if (USE_MOCK_DATA) {
    await mockDelay();
    return { ..._mockSustainabilityDefinition };
  }
  try {
    const params = fuarId ? { fuarId } : {};
    const res = await axios.get(`${BASE}/sustainability/definition`, { params, headers: getAuthHeader() });
    return res.data;
  } catch (e) { parseError(e); }
}

/**
 * Admin: Sustainability soru setini oluştur/güncelle.
 * scopeFuarId: null = global default, sayısal = o fuar için override.
 * POST /sustainability/definition
 */
export async function createSustainabilityDefinition(body) {
  if (USE_MOCK_DATA) { await mockDelay(); return { success: true }; }
  try {
    const res = await axios.post(`${BASE}/sustainability/definition`, body, { headers: getAuthHeader() });
    return res.data;
  } catch (e) { parseError(e); }
}

/**
 * Belirli bir (eski/inaktif) definition versiyonunu ID ile çek.
 * GET /sustainability/definition/{defId}
 */
export async function getSustainabilityDefinitionById(defId) {
  if (USE_MOCK_DATA) { await mockDelay(); return { ..._mockSustainabilityDefinition, id: defId }; }
  try {
    const res = await axios.get(`${BASE}/sustainability/definition/${defId}`, { headers: getAuthHeader() });
    return res.data;
  } catch (e) { parseError(e); }
}

/**
 * Proje başına sustainability form doldurma payload'u.
 * Tek çağrıda: generalInfo (read-only), definition, answers döner.
 * GET /{id}/sustainability
 */
export async function getProjectSustainability(id, { forceLive = false } = {}) {
  // WAS (until #712): `if (USE_MOCK_DATA)` with no forceLive, so the page showed the mock definition even for a real project.
  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();
    const p = _mockProjects.find(p => p.id === id);
    return {
      projectId: id,
      generalInfo: {
        exhibitorName: p?.firmaName ?? '—',
        contractorName: p?.dekoratorFirmaName ?? '—',
        standNumber: p?.standName ?? '—',
        standSize: null,
      },
      definition: { ..._mockSustainabilityDefinition },
      answers: _mockSustainabilityAnswers[id] ?? [],
      answeredDefinitionVersion: 1,
      updatedAt: new Date().toISOString(),
    };
  }
  try {
    const res = await axios.get(`${BASE}/${id}/sustainability`, { headers: getAuthHeader() });
    return res.data;
  } catch (e) { parseError(e); }
}

/**
 * Proje sustainability cevaplarını kaydet — FULL REPLACE.
 * ⚠️ Her seferinde tüm cevap dizisini gönder (sadece değişeni değil).
 * ⚠️ Her answer objesinde sadece o sorunun tipine ait tek field olmalı (null'lar temizlenmeli).
 * PUT /{id}/sustainability
 */
export async function saveProjectSustainability(id, answers, { forceLive = false } = {}) {
  // WAS (until #712): `if (USE_MOCK_DATA)` with no forceLive, so the answers of a real project were kept in memory and never saved.
  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();
    _mockSustainabilityAnswers[id] = answers;
    const p = _mockProjects.find(p => p.id === id);
    if (p) p.sustainability = { answers, updatedAt: new Date().toISOString() };
    return { success: true };
  }
  try {
    const res = await axios.put(`${BASE}/${id}/sustainability`, { answers }, { headers: getAuthHeader() });
    return res.data;
  } catch (e) { parseError(e); }
}

// ─────────────────────────────────────────────────────────────────────────────
// 9. REVİZYONLAR — konuşma + öneri tamponu (buffer)
// ─────────────────────────────────────────────────────────────────────────────
// 2026-08-24 backend yeniden şekillendirmesi. Kaynak: docs/guides/
// FE-Revisions-and-KVKK-update-guide.md (beyani-revisions-guide.md bölüm D + D2'nin
// yerine geçer, güncel model odur).
//
// Bir revizyon artık sadece "inceleyen bir şey buldu" değil, KABUL BEKLEYEN BİR
// DEĞİŞİKLİK ÖNERİSİ. Tek nesne iki yönü de taşır:
//
//   openedByRole=STAFF   → biz istedik.  Tampon boş.  OPEN     durumunda açılır.
//   openedByRole=COMPANY → onlar istedi. Tampon dolu. ANSWERED durumunda açılır
//                          (kendi değerini taşıyan bir talebin bekleyeceği bir şey
//                          yoktur — ilk andan itibaren top BİZDEDİR).
//
// Neyle ilgili olduğu `targetKind`:
//   DOCUMENT   → "şu zorunlu belgeyi tekrar yükleyin" (standFileId). Yanıt bir
//                metin değil YÜKLEMEDİR; dosyanın kendisi tampondur (approvalStatus
//                0 kalır), bu yüzden ikinci bir tampon icat edilmez.
//   SPEC_FIELD → tek bir beyan alanı (fieldKey, bkz. getRevisableFields). TAMPONLU
//                tür budur: değer `proposed`a yazılır ve KABUL EDİLENE KADAR
//                projede hiçbir şey değişmez.
//   FREEFORM   → makine tarafından hedeflenemeyen bulgu. Yazılacak değer yok,
//                sadece "kabul edildi" (ACKNOWLEDGED) ile kapanır.
//
// `type` (structural/visual) ESKİ iki yönlü ayrım — sadece tel uyumluluğu için
// duruyor (DOCUMENT→visual, diğerleri→structural). YENİ KOD targetKind OKUSUN.
//
// Yaşam döngüsü:
//     OPEN ──onlar yanıtlar──> ANSWERED ──biz kabul ederiz──> RESOLVED
//       ^                          │
//       └────biz reddederiz────────┘   (gerekçe konuşmaya düşer, tampon temizlenir)
//
// Reddetmek revizyonu ÖLDÜRMEZ, OPEN'a geri döndürür: istek hiç karşılanmadı, o
// yüzden elle yeniden açılması gereken ölü bir duruma düşmek yerine açık kalır.
//
// ⚠️ status artık ÜÇ değerli. "bitmemiş" testi `status !== 'resolved'` olmalı —
//    `status === 'open'` DEĞİL: ANSWERED da bitmemiştir, üstelik sıra BİZDEDİR.
//
// Projedeki iki denormalize bayrak (her yazımda yeniden türetilir, ikisi de aynı
// anda true olabilir — biri bulgu, diğeri talep demek, tek rozete katlanmazlar):
//   project.inRevision      → RESOLVED olmayan en az bir revizyon var
//   project.pendingProposal → ANSWERED en az bir revizyon var (sıra bizde)

const REVISION_TYPES = ['structural', 'visual'];

// Revize edilebilir beyan alanları — canlıda GET /revisable-fields'ten gelir,
// burası MOCK modun karşılığı (guide'ın "The revisable fields" tablosuyla birebir).
// ⚠️ Dropdown'ı ASLA sabit listeden kurma, getRevisableFields'ten kur: listede
// olmayan bir key'i backend'in her endpoint'i reddeder.
const _mockRevisableFields = [
  { key: 'standTypeId',          label: 'Stand tipi',                 valueType: 'INTEGER', mirroredToStand: false, requiresSystemReassign: true,  note: 'Kabul edilirse zorunlu belge seti yeniden belirlenmeli.' },
  { key: 'declaredHeightNumber', label: 'Beyan edilen yükseklik (m)', valueType: 'NUMBER',  mirroredToStand: false, requiresSystemReassign: true,  note: 'Kabul edilirse yükseklik bandı yeniden atanmalı.' },
  { key: 'declaredAreaM2',       label: 'Stand alanı (m²)',           valueType: 'NUMBER',  mirroredToStand: false, requiresSystemReassign: false, note: null },
  { key: 'declaredKw',           label: 'Elektrik gücü (kW)',         valueType: 'NUMBER',  mirroredToStand: false, requiresSystemReassign: false, note: null },
  { key: 'declaredGlass',        label: 'Cam kullanımı',              valueType: 'BOOLEAN', mirroredToStand: true,  requiresSystemReassign: false, note: 'Kabul stand_detail.cam_kullanimi alanını da günceller.' },
  { key: 'declaredPaint',        label: 'Boya kullanımı',             valueType: 'BOOLEAN', mirroredToStand: true,  requiresSystemReassign: false, note: 'Kabul stand_detail.boya_kullanimi alanını da günceller.' },
];

// Tel üzerinde `value` HER ZAMAN string'tir; backend alanın valueType'ına göre
// çevirir ve OKUYAMADIĞINI sessizce varsayılana çevirmez, 400 döner. Türkçe ondalık
// virgülü ("4,20") backend için okunamaz olduğundan burada noktaya çevriliyor —
// kullanıcı virgülle yazdı diye 400 almasın.
function _wireValue(value) {
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  const s = String(value ?? '').trim();
  return /^-?\d+,\d+$/.test(s) ? s.replace(',', '.') : s;
}

// MOCK tarafı için aynı çevrimin yerel karşılığı (canlıda backend yapar).
function _coerceMockValue(field, value) {
  if (!field) throw new Error('Bu alan revize edilebilir alanlar listesinde yok.');
  const raw = _wireValue(value);
  if (field.valueType === 'BOOLEAN') {
    const v = raw.toLocaleLowerCase('tr');
    if (['true', '1', 'evet', 'var'].includes(v)) return true;
    if (['false', '0', 'hayir', 'hayır', 'yok'].includes(v)) return false;
    throw new Error(`${field.label} evet/hayır olmalıdır (true|false|1|0): ${raw}`);
  }
  const n = Number(raw);
  if (raw === '' || Number.isNaN(n)) throw new Error(`${field.label} sayı olmalıdır: ${raw}`);
  if (field.valueType === 'INTEGER' && !Number.isInteger(n)) throw new Error(`${field.label} tam sayı olmalıdır: ${raw}`);
  return n;
}

// `proposed` tipli-nullable gelir: alanın tipine karşılık gelen slot dolu, diğerleri
// null. `bool: false` düşmesin diye ?? zinciri kullanılıyor (|| değil).
function _normalizeProposed(pr) {
  if (!pr) return null;
  return {
    value: pr.number ?? pr.integer ?? pr.bool ?? null,
    uploadedFileId: pr.uploadedFileId ?? null,
    note: pr.note ?? null,
    proposedBy: pr.proposedBy ?? null,
    proposedAt: pr.proposedAt ?? null,
  };
}

// Gerçek backend revizyonları mock'un beklediğinden farklı döner: UPPERCASE
// type/status, `_id` yerine `id`, ve messages[] yerine text (açılış mesajı) +
// responses[] (yanıtlar). Sayfa kodu lowercase/_id/messages[] beklediği için burada
// normalize edilir; 2026-08-24 alanları (targetKind/openedByRole/fieldKey/proposed/
// outcome/appliedValue) ise KORUNARAK geçirilir — tampon UI'ı bunlara bakar.
function _normalizeLiveRevision(r, project = null) {
  const type = (r.type || '').toLowerCase();
  // ⚠️ 2026-08-24 öncesi yazılmış her revizyonda targetKind NULL gelir (alan alt
  // dokümana sonradan eklendi, Mongo eski kayıtlar için null döner). Guide:
  // VISUAL → DOCUMENT, diğeri → FREEFORM.
  const targetKind = r.targetKind || (type === 'visual' ? 'DOCUMENT' : 'FREEFORM');
  const openedByRole = r.openedByRole || 'STAFF';
  const openerRole = openedByRole === 'COMPANY' ? 'company' : 'inspector';
  const otherRole = openedByRole === 'COMPANY' ? 'inspector' : 'company';

  const screenshot = r.screenshot
    ? {
        originalFileName: r.screenshot,
        _mockUrl: `${JAVA_BACKEND_API_URL.replace(/\/api\/v1$/, '')}/public/files/project_files/${r.screenshot}`,
      }
    : null;

  const messages = [
    // Açılış mesajı ayrı bir alanda (revision.text) durur, responses[] sadece
    // sonrasını taşır — prepend edilmezse söyleyecek sözü olan bir revizyon BOŞ
    // konuşma olarak render edilir.
    { _id: r.id, authorRole: openerRole, authorName: null, kind: 'message', uploadedFileName: null, text: r.text, createdAt: r.createdAt, userId: r.userId ?? null },
    ...(r.responses || []).map((resp) => ({
      _id: resp.id,
      // responses[] rol taşımaz. İki taraflı bir konuşmada "açanla aynı userId =
      // aynı taraf" SEZGİSEL olarak doğrudur; aynı taraftan ikinci bir kullanıcı
      // yazarsa balon yanlış hizalanır ama metin/tarih/yazar id'si doğrudur.
      authorRole: resp.userId != null && r.userId != null && resp.userId === r.userId ? openerRole : otherRole,
      authorName: null, kind: 'message', uploadedFileName: null,
      text: resp.text, createdAt: resp.createdAt, userId: resp.userId ?? null,
    })),
    // Belge revizyonunda dekoratörün yanıtı bir mesaj DEĞİL, yüklemedir:
    // uploadedFiles[].revisionId bu revizyona işaret eden dosyalar responses[]'te
    // GÖRÜNMEZ. Konuşmaya burada karıştırılmazsa tekrar yüklenen dosya kaybolur.
    ...(project?.uploadedFiles || [])
      .filter((f) => f.revisionId != null && String(f.revisionId) === String(r.id))
      .map((f) => ({
        _id: `upload-${f.id ?? f.storedFileName}`,
        authorRole: 'system', authorName: i18n.t("services:projectService.dekorator"), kind: 'file_upload',
        uploadedFileName: f.originalFileName ?? f.storedFileName,
        text: i18n.t("services:projectService.yeniDosyaYuklendi", { v: f.originalFileName ?? f.storedFileName ?? '' }),
        createdAt: f.uploadedAt ?? f.createdAt ?? null,
      })),
  ].sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0));

  return {
    _id: r.id,
    type,
    targetKind,
    openedByRole,
    fieldKey: r.fieldKey ?? null,
    standFileId: r.standFileId ?? null,
    text: r.text,
    screenshot,
    status: (r.status || '').toLowerCase(),
    userId: r.userId ?? null,
    createdAt: r.createdAt,
    updatedAt: r.updatedAt,
    resolvedBy: r.resolvedBy ?? null,
    resolvedAt: r.resolvedAt ?? null,
    proposed: _normalizeProposed(r.proposed),
    outcome: r.outcome ?? null,
    appliedValue: r.appliedValue ?? null,
    messages,
  };
}

function _normalizeLiveProject(p) {
  if (!p) return p;
  return { ...p, revisions: (p.revisions || []).map((r) => _normalizeLiveRevision(r, p)) };
}

// MOCK yardımcıları — mock revizyonlar zaten normalize şekilde (lowercase, _id,
// messages[]) tutulduğu için sadece bayrakların yeniden türetilmesi gerekir.
function _mockFindRevision(id, revisionId) {
  const p = _mockProjects.find((p) => p.id === id);
  if (!p) throw new Error('Proje bulunamadı');
  const revision = p.revisions?.find((r) => r._id === revisionId);
  if (!revision) throw new Error('Revizyon bulunamadı');
  return { p, revision };
}

// inRevision/pendingProposal canlıda backend'in her yazımda yeniden türettiği
// denormalize aynalar — mock tarafında da aynı anda güncel kalsınlar diye.
function _rederiveMockRevisionFlags(p) {
  const threads = (p.revisions || []).filter((r) => Array.isArray(r.messages));
  p.inRevision = threads.some((r) => r.status !== 'resolved');
  p.pendingProposal = threads.some((r) => r.status === 'answered');
}

function _mockTouch(p, revision) {
  const now = new Date().toISOString();
  if (revision) revision.updatedAt = now;
  p.updatedAt = now;
  _rederiveMockRevisionFlags(p);
  return now;
}

function _mockPushSystemMessage(revision, text) {
  revision.messages = [...(revision.messages || []), {
    _id: `msg-${Date.now()}-${revision.messages?.length ?? 0}`,
    authorRole: 'system', authorName: 'Sistem', kind: 'message', uploadedFileName: null,
    text, createdAt: new Date().toISOString(),
  }];
}

/**
 * Revize edilebilir beyan alanları (6 tane). Dropdown'ı BUNDAN kur — sabit listeden
 * değil; listede olmayan bir key'i /change-requests, /proposal ve /accept reddeder.
 * Anahtarlar KuralIhlaliDTO.field ile birebir aynı, o yüzden bir kural ihlali
 * çeviri yapılmadan "bu alanın revizyonunu iste"ye dönüştürülebilir.
 * Çalışma anında değişmez — çağıran taraf cache'leyebilir.
 * GET /projects/revisable-fields
 */
export async function getRevisableFields({ forceLive = false } = {}) {
  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();
    return _mockRevisableFields.map((f) => ({ ...f }));
  }
  try {
    const res = await axios.get(`${BASE}/revisable-fields`, { headers: getAuthHeader() });
    return res.data;
  } catch (e) { parseError(e); }
}

/**
 * Yeni revizyon konuşması başlatır — BİZ onlardan bir şey isteriz. OPEN durumunda,
 * tampon boş, openedByRole=STAFF açılır.
 * targetKind gönderilmez, TÜRETİLİR: visual → DOCUMENT, fieldKey varsa → SPEC_FIELD,
 * yoksa → FREEFORM. Bir revizyon ya dosyayı ya alanı hedefler, İKİSİNİ BİRDEN değil.
 * ⚠️ Oluşturulan revizyonu döner (yeni _id dahil) — UI hemen konuşma sayfasına
 * (`/Management/Projects/Stands/{standId}/revisions/{_id}`) yönlendirir.
 * POST /{id}/revisions (multipart: type, standFileId?, fieldKey?, text, screenshot?)
 */
export async function createRevision(id, type, text, { standFileId = null, fieldKey = null, screenshotFile = null, authorName = 'FDS Uzmanı', forceLive = false } = {}) {
  if (!REVISION_TYPES.includes(type)) throw new Error(i18n.t("services:projectService.gecersizRevizyonTipi", { type }));
  if (type === 'visual' && !standFileId) throw new Error(i18n.t("services:projectService.gorselRevizyonIcinBirBelge"));
  if (type === 'visual' && fieldKey) throw new Error(i18n.t("services:projectService.birRevizyonYaBelgeyiYa"));

  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();
    const p = _mockProjects.find(p => p.id === id);
    if (!p) throw new Error('Proje bulunamadı');
    const now = new Date().toISOString();
    const revision = {
      _id: `rev-${Date.now()}`,
      type,
      targetKind: type === 'visual' ? 'DOCUMENT' : (fieldKey ? 'SPEC_FIELD' : 'FREEFORM'),
      openedByRole: 'STAFF',
      fieldKey: fieldKey || null,
      standFileId: type === 'visual' ? Number(standFileId) : null,
      screenshot: screenshotFile ? { originalFileName: screenshotFile.name, _mockUrl: URL.createObjectURL(screenshotFile) } : null,
      status: 'open',
      proposed: null, outcome: null, appliedValue: null,
      messages: [
        { _id: `msg-${Date.now()}`, authorRole: 'inspector', authorName, kind: 'message', uploadedFileName: null, text, createdAt: now },
      ],
      createdAt: now, createdBy: authorName, updatedAt: now, resolvedAt: null,
    };
    // En yeni önce — canlı backend revisions[]'i böyle döner, mock da aynı sırada
    // dursun ki "yeni açılan revizyon revisions[0]" varsayımı iki modda da tutsun.
    p.revisions = [revision, ...(p.revisions || [])];
    _mockTouch(p, null);
    return revision;
  }
  try {
    const formData = new FormData();
    formData.append('type', type.toUpperCase());
    formData.append('text', text);
    if (standFileId) formData.append('standFileId', standFileId);
    if (fieldKey) formData.append('fieldKey', fieldKey);
    if (screenshotFile) formData.append('screenshot', screenshotFile);
    const res = await axios.post(`${BASE}/${id}/revisions`, formData, {
      headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' },
    });
    // Gerçek endpoint {success:true}/çıplak revizyon değil, TÜM proje dokümanını
    // döner — yeni revizyon her zaman revisions[0] (en yeni önce).
    const project = res.data;
    return _normalizeLiveRevision(project.revisions[0], project);
  } catch (e) { parseError(e); }
}

/**
 * ONLAR bir değişiklik talep eder — dekoratör tarafının revizyon AÇMA yolu.
 * Doğrudan ANSWERED durumunda, tamponu DOLU olarak açılır (openedByRole=COMPANY,
 * targetKind=SPEC_FIELD): kendi değerini taşıyan bir talebin bekleyeceği bir şey
 * yoktur, ilk andan itibaren sıra staff'tadır.
 * ⚠️ Proje kabul edildikten (projeBeyani.systemAssigned) SONRA beyanı değiştirmenin
 * TEK yolu budur — /declare o noktadan sonra 400 döner.
 * 400 {message, violations[]} → istenen değer fuarın kurallarını çiğniyor; bir gün
 * sonra staff reddetsin diye tampona park edilmez, anında reddedilir.
 * POST /{id}/change-requests?fieldKey=&value=&note=
 */
export async function createChangeRequest(id, fieldKey, value, { note = null, forceLive = false } = {}) {
  if (!fieldKey) throw new Error(i18n.t("services:projectService.degisiklikTalebiIcinBirAlan"));

  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();
    const p = _mockProjects.find(p => p.id === id);
    if (!p) throw new Error('Proje bulunamadı');
    const field = _mockRevisableFields.find(f => f.key === fieldKey);
    const coerced = _coerceMockValue(field, value);
    const now = new Date().toISOString();
    const text = note || `${field.label} için değişiklik talebi: ${coerced}`;
    p.revisions = [{
      _id: `rev-${Date.now()}`,
      type: 'structural', targetKind: 'SPEC_FIELD', openedByRole: 'COMPANY',
      fieldKey, standFileId: null, screenshot: null, status: 'answered',
      proposed: { value: coerced, uploadedFileId: null, note, proposedBy: null, proposedAt: now },
      outcome: null, appliedValue: null,
      messages: [{ _id: `msg-${Date.now()}`, authorRole: 'company', authorName: 'Dekoratör', kind: 'message', uploadedFileName: null, text, createdAt: now }],
      createdAt: now, createdBy: 'Dekoratör', updatedAt: now, resolvedAt: null,
    }, ...(p.revisions || [])];
    _mockTouch(p, null);
    return _enrichProject(p);
  }
  try {
    const res = await axios.post(`${BASE}/${id}/change-requests`, null, {
      params: { fieldKey, value: _wireValue(value), ...(note ? { note } : {}) },
      headers: getAuthHeader(),
    });
    return _normalizeLiveProject(res.data);
  } catch (e) { parseError(e); }
}

/**
 * ONLAR bizim açtığımız revizyona bir değer önerir — TAMPONU DOLDURUR, durumu
 * ANSWERED'a taşır. ⚠️ Projede HİÇBİR ŞEY DEĞİŞMEZ; bu çağrının sözleşmesinin
 * tamamı budur. Yeni bir öneri masadakinin YERİNE geçer; geri çekilen değer önce
 * konuşmaya düşer, böylece tarihçe thread'de yaşar ve `proposed` her zaman sadece
 * "şu an masada olan"dır.
 * 400 → kapatılmış revizyon · belge revizyonu (dosyayı yükleyin) · beyan alanı
 * olmayan (FREEFORM) revizyon · kural ihlali {message, violations[]}.
 * POST /{id}/revisions/{revisionId}/proposal?value=&note=
 */
export async function proposeRevisionValue(id, revisionId, value, { note = null, forceLive = false } = {}) {
  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();
    const { p, revision } = _mockFindRevision(id, revisionId);
    if (revision.status === 'resolved') throw new Error('Kapatılmış bir revizyona öneri girilemez.');
    if (revision.targetKind === 'DOCUMENT') throw new Error('Bu bir belge revizyonu — dosyayı yükleyin.');
    if (!revision.fieldKey) throw new Error('Bu revizyonun bir beyan alanı yok — bunun yerine mesaj gönderin.');
    const field = _mockRevisableFields.find(f => f.key === revision.fieldKey);
    const coerced = _coerceMockValue(field, value);
    if (revision.proposed) _mockPushSystemMessage(revision, `Önceki öneri geri çekildi: ${revision.proposed.value}`);
    const now = new Date().toISOString();
    revision.proposed = { value: coerced, uploadedFileId: null, note, proposedBy: null, proposedAt: now };
    revision.status = 'answered';
    revision.messages = [...revision.messages, {
      _id: `msg-${Date.now()}`, authorRole: 'company', authorName: 'Dekoratör', kind: 'message',
      uploadedFileName: null, text: note || `Önerilen değer: ${coerced}`, createdAt: now,
    }];
    _mockTouch(p, revision);
    return _enrichProject(p);
  }
  try {
    const res = await axios.post(`${BASE}/${id}/revisions/${revisionId}/proposal`, null, {
      params: { value: _wireValue(value), ...(note ? { note } : {}) },
      headers: getAuthHeader(),
    });
    return _normalizeLiveProject(res.data);
  } catch (e) { parseError(e); }
}

/**
 * BİZ kabul ederiz — önerilen bir değerin projeye ulaşmasının sistemdeki TEK yolu.
 *   parametresiz  → APPLIED            : onların değeri olduğu gibi yazılır
 *   value=4.10    → APPLIED_WITH_EDIT  : biz KENDİ değerimizi yazarız (onlarınki
 *                                        tamponda kalır, bizimki appliedValue'ya)
 *   apply=false   → ACKNOWLEDGED       : kabul edildi, hiçbir şey yazılmadı.
 *                                        DOCUMENT/FREEFORM için TEK seçenek budur.
 * value + apply=false birlikte gönderilemez (400) — birini seç.
 * Revizyon kapanır (RESOLVED), resolvedBy/resolvedAt damgalanır ve ne olduğunu
 * anlatan bir satır konuşmaya düşer (timeline sessizce kesilmesin diye).
 * ⚠️ standTypeId veya declaredHeightNumber kabul etmek projeyi SİSTEM ATAMASI
 *    kuyruğuna geri gönderir (requiresSystemReassign): systemAssignedAt temizlenir,
 *    awaitingSystemAssignment tekrar true olur. Zorunlu belgeler OTOMATİK yeniden
 *    çözülmez — kabul etmeden önce staff'ı uyar.
 * ⚠️ Belge revizyonunu kabul etmek DOSYAYI ONAYLAMAZ; dosya onayı başlık bazlı,
 *    kendi endpoint'inde ayrı bir eylemdir.
 * 400 {message, violations[]} → değer kuralları çiğniyor. Öneri anında değil,
 * BURADA da yeniden yargılanır: aradan geçen sürede değişen bir kural eski bir
 * öneriyle atlatılamasın diye.
 * POST /{id}/revisions/{revisionId}/accept?value=&apply=
 */
export async function acceptRevision(id, revisionId, { value = null, apply = true, forceLive = false } = {}) {
  const hasEdit = value != null && String(value).trim() !== '';
  if (hasEdit && apply === false) {
    throw new Error(i18n.t("services:projectService.kendiDegeriniziYazmakIleProjeye"));
  }

  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();
    const { p, revision } = _mockFindRevision(id, revisionId);
    const isWritable = revision.targetKind === 'SPEC_FIELD' && !!revision.fieldKey;
    if (hasEdit && !isWritable) throw new Error('Bu revizyonda yazılacak bir beyan alanı yok.');
    const field = isWritable ? _mockRevisableFields.find(f => f.key === revision.fieldKey) : null;
    let outcome = 'ACKNOWLEDGED';
    let applied = null;
    if (isWritable && apply !== false) {
      applied = hasEdit ? _coerceMockValue(field, value) : (revision.proposed?.value ?? null);
      if (applied == null) throw new Error('Kabul edilecek bir öneri yok.');
      outcome = hasEdit ? 'APPLIED_WITH_EDIT' : 'APPLIED';
      p.projeBeyani = { ...(p.projeBeyani || {}), [revision.fieldKey]: applied };
      // Cam/boya haritada da yaşıyor (backend #53) — iki kopya ayrışmasın.
      const kullanimKod = { declaredGlass: 'CAM', declaredPaint: 'BOYA' }[revision.fieldKey];
      if (kullanimKod) {
        p.projeBeyani.declaredKullanimlar = {
          ...(p.projeBeyani.declaredKullanimlar || {}), [kullanimKod]: applied,
        };
      }
      if (field?.requiresSystemReassign) {
        p.projeBeyani.systemAssigned = false;
        p.projeBeyani.systemAssignedAt = null;
        p.projeBeyani.awaitingSystemAssignment = true;
      }
    }
    const now = new Date().toISOString();
    revision.status = 'resolved';
    revision.resolvedAt = now;
    revision.outcome = outcome;
    revision.appliedValue = applied != null ? String(applied) : null;
    _mockPushSystemMessage(revision, outcome === 'ACKNOWLEDGED'
      ? 'Revizyon kabul edildi (projeye bir değer yazılmadı).'
      : `Revizyon kabul edildi — uygulanan değer: ${applied}${outcome === 'APPLIED_WITH_EDIT' ? ' (düzenlenerek)' : ''}`);
    _mockTouch(p, revision);
    return _enrichProject(p);
  }
  try {
    const params = {};
    if (hasEdit) params.value = _wireValue(value);
    if (apply === false) params.apply = false;
    const res = await axios.post(`${BASE}/${id}/revisions/${revisionId}/accept`, null, { params, headers: getAuthHeader() });
    return _normalizeLiveProject(res.data);
  } catch (e) { parseError(e); }
}

/**
 * BİZ reddederiz — tampon temizlenir, gerekçe konuşmaya düşer, revizyon OPEN'a
 * DÖNER (ölmez: istek karşılanmadı, dolayısıyla açık kalır).
 * `reason` ZORUNLU: OPEN'a döndürmenin tüm amacı onların bir şey yapabilmesi, ve
 * gerekçesiz "reddedildi" üzerine iş yapılabilecek bir şey değildir.
 * Bir talebi revize ettirmek yerine tümden reddetmek için bunun yerine KAPAT:
 * setRevisionStatus(id, revisionId, 'resolved').
 * POST /{id}/revisions/{revisionId}/reject?reason=
 */
export async function rejectRevision(id, revisionId, reason, { forceLive = false } = {}) {
  if (!reason || !String(reason).trim()) throw new Error(i18n.t("services:projectService.reddetmeGerekcesiZorunludur"));

  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();
    const { p, revision } = _mockFindRevision(id, revisionId);
    if (!revision.proposed) throw new Error('Reddedilecek bir öneri yok.');
    revision.proposed = null;
    revision.status = 'open';
    _mockPushSystemMessage(revision, `Öneri reddedildi: ${String(reason).trim()}`);
    _mockTouch(p, revision);
    return _enrichProject(p);
  }
  try {
    const res = await axios.post(`${BASE}/${id}/revisions/${revisionId}/reject`, null, {
      params: { reason: String(reason).trim() }, headers: getAuthHeader(),
    });
    return _normalizeLiveProject(res.data);
  } catch (e) { parseError(e); }
}

/**
 * Bir revizyon konuşmasına yeni mesaj ekler. İKİ TARAF DA kullanabilir (authorRole
 * mock tarafında hangi balonun hangi tarafa hizalanacağını belirler; canlıda rol
 * yazılmaz, X-user-Id'den türetilir).
 * ⚠️ Hiçbir şeyi ÇÖZMEZ, tamponu DOLDURMAZ — sadece responses[]'e ekler. Bir değer
 * önermek için proposeRevisionValue kullan.
 * POST /{id}/revisions/{revisionId}/messages?text={text}
 */
export async function addRevisionMessage(id, revisionId, text, authorName = 'FDS Uzmanı', { authorRole = 'inspector', forceLive = false } = {}) {
  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();
    const { p, revision } = _mockFindRevision(id, revisionId);
    const now = new Date().toISOString();
    revision.messages = [...revision.messages, { _id: `msg-${Date.now()}`, authorRole, authorName, kind: 'message', uploadedFileName: null, text, createdAt: now }];
    _mockTouch(p, revision);
    return { success: true };
  }
  try {
    const res = await axios.post(`${BASE}/${id}/revisions/${revisionId}/messages`, null, { params: { text }, headers: getAuthHeader() });
    return _normalizeLiveProject(res.data);
  } catch (e) { parseError(e); }
}

/**
 * Revizyonu çöz/yeniden aç — durum değişimini konuşmada görünür kılmak için
 * otomatik bir 'system' mesajı eklenir (mock tarafında).
 * ⚠️ Bu yol `outcome` YAZMAZ — verdict kaydı sadece /accept'ten çıkar. Bir talebe
 * "hayır" deyip kapatmanın doğru yolu budur; "şunu düzeltin" demek için
 * rejectRevision kullanılır (o OPEN'a döndürür).
 * PUT /{id}/revisions/{revisionId}/status?status={open|resolved}
 */
export async function setRevisionStatus(id, revisionId, status, { forceLive = false } = {}) {
  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();
    const { p, revision } = _mockFindRevision(id, revisionId);
    const now = new Date().toISOString();
    revision.status = status;
    revision.resolvedAt = status === 'resolved' ? now : null;
    _mockPushSystemMessage(revision, status === 'resolved' ? 'Revizyon çözüldü olarak işaretlendi.' : 'Revizyon yeniden açıldı.');
    _mockTouch(p, revision);
    return { success: true };
  }
  try {
    const res = await axios.put(`${BASE}/${id}/revisions/${revisionId}/status`, null, { params: { status }, headers: getAuthHeader() });
    return _normalizeLiveProject(res.data);
  } catch (e) { parseError(e); }
}

/**
 * Bir revizyon konuşmasını komple sil — mesajlar, tampon, her şey.
 * ⚠️ Sahiplik kontrolü, soft delete ve onay kapısı YOK. Dekoratörün yanıtladığı bir
 * revizyonu silmek ONLARIN YANITINI da yok eder — UI'da onay modalıyla korunur.
 * ⚠️ Bilinmeyen bir revisionId 404 DEĞİLDİR: backend diziyi filtreleyip kaydeder,
 * yanlış id 200 + değişmemiş doküman döner.
 * DELETE /{id}/revisions/{revisionId}
 */
export async function deleteRevision(id, revisionId, { forceLive = false } = {}) {
  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();
    const p = _mockProjects.find(p => p.id === id);
    if (p) {
      p.revisions = (p.revisions || []).filter(r => r._id !== revisionId);
      _mockTouch(p, null);
    }
    return { success: true };
  }
  try {
    const res = await axios.delete(`${BASE}/${id}/revisions/${revisionId}`, { headers: getAuthHeader() });
    return _normalizeLiveProject(res.data);
  } catch (e) { parseError(e); }
}

/**
 * ESKİ (düz not) revizyon ekleme — createRevision'dan ÖNCE buradaydı, inceleyen kişi
 * tarafının revizyon akışı Stand Listesi'ne taşınırken (StandOverviewDetailPage.jsx /
 * StandRevisionDetailPage.jsx, bkz. bölüm 11) ProjectDetailPage.jsx/ProjectsListPage.jsx
 * bu modülün dışında başka bir geliştiricinin sayfaları olduğu için dokunulmadı ve hâlâ
 * bunu çağırıyor — geriye dönük çalışsın diye korunuyor. Yeni kod bunu ÇAĞIRMASIN.
 * ⚠️ Ürettiği düz {_id,text,createdAt,createdBy} nesnesi createRevision'ın
 * {targetKind,status,messages[]} şeklinden farklı — aynı p.revisions dizisini
 * paylaşıyorlar, bu yüzden thread tüketicileri (RevisionThread, StandOverviewDetailPage'in
 * Revizyonlar sekmesi, StandRevisionDetailPage) sadece Array.isArray(r.messages) olan
 * öğeleri geçerli bir konuşma sayar.
 * ⚠️ Backend'de `type` parametresinin YOKLUĞU ayrı bir handler'a (params="!type")
 * gider; JSON gövde göndermek `text`i de görünmez kılar → 400.
 * POST /{id}/revisions?text={text}
 */
export async function addRevision(id, text) {
  if (USE_MOCK_DATA) {
    await mockDelay();
    const p = _mockProjects.find(p => p.id === id);
    if (p) p.revisions = [...(p.revisions || []), { _id: `rev-${Date.now()}`, text, createdAt: new Date().toISOString(), createdBy: 'mock' }];
    return { success: true };
  }
  try {
    const res = await axios.post(`${BASE}/${id}/revisions`, null, { params: { text }, headers: getAuthHeader() });
    return res.data;
  } catch (e) { parseError(e); }
}

// ─────────────────────────────────────────────────────────────────────────────
// 10. SİLME
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Soft delete — listelerden gizler, veri korunur.
 * ⚠️ UI'daki "Sil" butonu her zaman bunu kullanmalı.
 * DELETE /{id}
 */
export async function deleteProject(id) {
  if (USE_MOCK_DATA) {
    await mockDelay();
    _mockProjects = _mockProjects.filter(p => p.id !== id);
    return { success: true };
  }
  try {
    const res = await axios.delete(`${BASE}/${id}`, { headers: getAuthHeader() });
    return res.data;
  } catch (e) { parseError(e); }
}

/**
 * ⚠️ Hard delete — GERİ ALINAMAZ. Mongo dokümanı + disk dosyaları silinir.
 * Normal kullanıcı akışına BAĞLAMA — sadece test/temizlik için.
 * DELETE /{id}/hard
 */
export async function deleteProjectHard(id) {
  if (USE_MOCK_DATA) {
    await mockDelay();
    _mockProjects = _mockProjects.filter(p => p.id !== id);
    return { success: true };
  }
  try {
    const res = await axios.delete(`${BASE}/${id}/hard`, { headers: getAuthHeader() });
    return res.data;
  } catch (e) { parseError(e); }
}

// ─────────────────────────────────────────────────────────────────────────────
// 11. FİRMA KÖPRÜSÜ (standId — standOverviewService.js ile tek ortak alan)
// ─────────────────────────────────────────────────────────────────────────────
// projectService.js ve standOverviewService.js birbirinden TAMAMEN bağımsız iki mock
// veri seti (farklı id şemaları — 'mock-xxx' vs mongo-style). Firma tarafı sayfaları
// (FirmaStandDetailPage.jsx, FirmaProjelerListPage.jsx) standOverviewService verisini
// kullanır; bu iki fonksiyon dekoratöre salt-okunur revizyon görünürlüğü vermek için
// standId üzerinden köprü kurar. Servis dosyaları birbirini import ETMEZ — köprü,
// her iki servisi de zaten import eden SAYFA seviyesinde çağrılır.

/**
 * standOverviewService.js standId'sinden bu modülün projesini bulur.
 * ⚠️ Diğer tüm export'ların aksine bulunamazsa THROW ETMEZ, null döner — çoğu standın
 * bağlı bir projesi olmayacağı için bu "yumuşak" bir arama.
 * ⚠️ Çağıran taraf standId'yi HER ZAMAN zaten çözülmüş bir stand nesnesinin standId
 * alanından versin, route param'ından değil (bkz. dosya başı KURAL).
 * GET /projects/by-stand/{standId}
 */
export async function getProjectByStandId(standId, { forceLive = false } = {}) {
  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();
    const p = _mockProjects.find(p => Number(p.standId) === Number(standId));
    return p ? _enrichProject(p) : null;
  }
  try {
    const res = await axios.get(`${BASE}/by-stand/${standId}`, { headers: getAuthHeader() });
    return _normalizeLiveProject(res.data);
  } catch (e) {
    if (e.response?.status === 404) return null;
    parseError(e);
  }
}

/**
 * BİTMEMİŞ (status !== 'resolved') revizyon konuşması olan standId'lerin listesi —
 * StandListPage.jsx satırındaki ‼ rozeti için TEK bir toplu sorgu (satır başına ayrı
 * bir getProjectByStandId çağrısı yerine). Aynı standId köprüsünü kullanır ama
 * Firma'ya özel değil — inceleyen kişi tarafı (Stand Listesi) da buradan okur.
 * ⚠️ "açık" burada OPEN durumu değil, "kapanmamış" demek: ANSWERED de sayılır.
 * GET /projects/open-revision-stand-ids
 */
export async function getOpenRevisionStandIds({ forceLive = false } = {}) {
  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();
    return _mockProjects
      .filter(p => p.standId != null && (p.revisions || []).some(isRevisionUnfinished))
      .map(p => Number(p.standId));
  }
  try {
    const res = await axios.get(`${BASE}/open-revision-stand-ids`, { headers: getAuthHeader() });
    return res.data;
  } catch (e) { parseError(e); }
}

/**
 * Açık revizyon konuşması SAYISI, standId'ye göre — StandListPage.jsx satırındaki
 * "Revizyon İstenen" sayaç kutusu için, getOpenRevisionStandIds ile aynı gerekçeyle
 * (satır başına ayrı bir getProjectByStandId çağrısı yerine) TEK bir toplu sorgu.
 * getOpenRevisionStandIds'ten farkı: id listesi değil, standId → sayı map'i döner
 * (açık revizyonu olmayan standId'ler için anahtar hiç yoktur, UI'da ?? 0 ile okunur).
 * GET /projects/open-revision-counts
 */
export async function getOpenRevisionCounts({ forceLive = false } = {}) {
  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();
    const counts = {};
    _mockProjects.forEach(p => {
      if (p.standId == null) return;
      const openCount = (p.revisions || []).filter(isRevisionUnfinished).length;
      if (openCount > 0) counts[p.standId] = openCount;
    });
    return counts;
  }
  try {
    const res = await axios.get(`${BASE}/open-revision-counts`, { headers: getAuthHeader() });
    return res.data;
  } catch (e) { parseError(e); }
}

/**
 * standId → ANSWERED revizyon sayısı — "SIRA BİZDE" tepsisi. getOpenRevisionCounts'un
 * kardeşi ama farklı bir soruyu yanıtlar: o "bu stand hâlâ üzerinde çalışılıyor mu",
 * bu "bu stand BENİ mi bekliyor". Asıl iş kuyruğu budur; diğeri bir durum rozeti.
 * GET /projects/pending-proposal-counts
 */
export async function getPendingProposalCounts({ forceLive = false } = {}) {
  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();
    const counts = {};
    _mockProjects.forEach(p => {
      if (p.standId == null) return;
      const pending = (p.revisions || []).filter(isRevisionAwaitingUs).length;
      if (pending > 0) counts[p.standId] = pending;
    });
    return counts;
  }
  try {
    const res = await axios.get(`${BASE}/pending-proposal-counts`, { headers: getAuthHeader() });
    return res.data;
  } catch (e) { parseError(e); }
}

// #713: the five corner types are found by their file_type ids, never by display name (a staff rename on Dosya Türleri, or a catalogue
// that lacks a name, used to leave fewer than four and the wizard fell back to the mock ids 1-4). The ids are the ones the backend
// locks (ProjeGorselConstant / FileTypeConstant.KODDA_KULLANILAN: renamed only, never moved or deleted); the same constants the
// taahhütname helpers already read. A row counts only while it still sits under Proje (file_type 2), so an id that drifted onto
// another type is not mistaken for a corner.
const PROJE_FILE_TYPE_ID = 2;
const CORNER_SLOTS = [
  ...KOSE_SUB_IDS.map((fileTypeId, i) => ({ name: `3D Köşe ${i + 1}`, fileTypeId })),
  { name: 'Kuşbakışı', fileTypeId: KUSBAKISI_SUB_ID },
];

/**
 * "Proje Görselleri" (standFileId 2) yüklemeleri için köşe/kuşbakışı alt-tipleri —
 * bunlar stand_file tablosunda değil, AYRI bir file_type tablosunda yaşıyor
 * (document-templates controller, GET /document-templates/file-types),
 * bu yüzden /stand-files-classic'te görünmezler.
 * Sonuç: bulunabilen köşeler, sırayla [{ name, fileTypeId }] (3D Köşe 1-4, Kuşbakışı). Eksik olan köşe listede YOKTUR;
 * dört köşeyi isteyen çağıran, dördü yoksa yüklemeyi reddetmelidir (FirmaProjelerListPage.jsx).
 */
export async function getProjeGorselleriCorners() {
  try {
    // WAS (until #503): params: { parentId: 0 } -- the backend now reads that as "children of 0", i.e. only the 14 root categories, so
    // none of the corner types came back, every live slot had `subStandFileId` undefined and the template examples (81-84) could not
    // be found. No parentId = the whole catalogue (~90 rows), matched by name below.
    const res = await axios.get(`${JAVA_BACKEND_API_URL}/document-templates/file-types`, {
      headers: getAuthHeader(),
    });
    // WAS (until #713): matched by fileTypeName ('3D Köşe 1' ... 'Kuşbakışı') against the whole catalogue.
    const byId = new Map((res.data || []).map((f) => [Number(f.fileTypeId), f]));
    return CORNER_SLOTS
      .filter((slot) => Number(byId.get(slot.fileTypeId)?.parentId) === PROJE_FILE_TYPE_ID)
      .map(({ name, fileTypeId }) => ({ name, fileTypeId }));
  } catch (e) { parseError(e); }
}

/**
 * Dekoratör standOverviewService.uploadStandFile ile bir dosya yüklediğinde, o belge
 * tipinde BİTMEMİŞ bir belge (visual/DOCUMENT) revizyonu varsa konuşmaya otomatik bir
 * 'file_upload' mesajı ekler — dekoratörün metin kutusu olmayan "yanıtı" budur.
 * ⚠️ 2026-08-24: yükleme aynı zamanda revizyonu OPEN → ANSWERED taşır. Dosya ONLARIN
 * YANITIDIR, dolayısıyla önerilen bir değer gibi staff tepsisine düşer. Asla otomatik
 * RESOLVED olmaz: staff inceleyip kabul eder, kapının tüm anlamı budur.
 * ⚠️ Belge revizyonunu kabul etmek dosyayı ONAYLAMAZ — dosya onayı başlık bazlı,
 * ayrı bir eylemdir.
 * Eşleşme yoksa (bağlı proje yok veya o standFileId'de bitmemiş revizyon yok)
 * sessizce hiçbir şey yapmaz. FirmaStandDetailPage.jsx'in handleUpload'ı tarafından,
 * uploadStandFile başarılı olduktan hemen sonra çağrılır.
 */
export async function notifyDecoratorFileUpload(standId, standFileId, fileInfo) {
  if (USE_MOCK_DATA) {
    await mockDelay();
    const p = _mockProjects.find(p => Number(p.standId) === Number(standId));
    const revision = p?.revisions?.find(r => r.type === 'visual' && isRevisionUnfinished(r) && r.standFileId === Number(standFileId));
    if (!revision) return { linked: false };
    const now = new Date().toISOString();
    revision.messages = [...revision.messages, {
      _id: `msg-${Date.now()}`, authorRole: 'system', authorName: p.dekoratorFirmaName || 'Dekoratör',
      kind: 'file_upload', uploadedFileName: fileInfo?.originalFileName ?? null,
      text: `Yeni dosya yüklendi: ${fileInfo?.originalFileName ?? ''}`, createdAt: now,
    }];
    revision.status = 'answered';
    _mockTouch(p, revision);
    return { linked: true, revisionId: revision._id };
  }
  try {
    const res = await axios.post(`${BASE}/by-stand/${standId}/files/${standFileId}/notify`, null, { headers: getAuthHeader() });
    return res.data;
  } catch (e) { parseError(e); }
}

// ─────────────────────────────────────────────────────────────────────────────
// YARDIMCI SABİTLER
// ─────────────────────────────────────────────────────────────────────────────

export const PROJECT_STATUS = {
  1: { get label() { return i18n.t("services:projectService.aktif"); },       color: 'success' },
  2: { get label() { return i18n.t("services:projectService.beklemede"); },   color: 'warning' },
  3: { get label() { return i18n.t("services:projectService.reddedildi"); },  color: 'danger'  },
  4: { get label() { return i18n.t("services:projectService.tamamlandi"); },  color: 'info'    },
};

export const DEAL_STATUS = {
  1: { get label() { return i18n.t("services:projectService.anlasmaVar"); },   color: 'success'   },
  2: { get label() { return i18n.t("services:projectService.yok"); },           color: 'secondary' },
  3: { get label() { return i18n.t("services:projectService.katilmiyor"); },    color: 'danger'    },
};

export const FINANCE_APPROVAL = {
  0: { get label() { return i18n.t("services:projectService.beklemede"); },    color: 'warning'   },
  1: { get label() { return i18n.t("services:projectService.onaylandi"); },    color: 'success'   },
  2: { get label() { return i18n.t("services:projectService.onaylanmadi"); },  color: 'danger'    },
};

// ═════════════════════════════════════════════════════════════════════════════
// 12. PROJE ÖZEL İZNİ (proje_ozel_izin) — 2026-08-28
//     Kaynak: docs/guides/FE-ProjectController-ozel-izin-guide.md
// ═════════════════════════════════════════════════════════════════════════════
/**
 * Bir Proje Özel İzni = "bu fuarın kurallarını ÇİĞNEYEN bir beyanı yine de KAYDETME"
 * izni. 2026-08-28 öncesinde ihlalli beyan reddediliyordu ve HİÇBİR ŞEY saklanmıyordu;
 * 5,00 m'lik salonda gerçekten 5,50 m'ye ihtiyacı olan katılımcının talebini
 * koyacağı bir yer yoktu, pazarlık telefonda yapılıyordu.
 *
 * DÖRT KURAL:
 * 1. BİR İZİN BEYANIN TAMAMINI KAPSAR — kural başına değil, alan başına değil.
 *    `ihlalKodlari` talep anındaki fotoğraftır: KAYIT, filtre değil. Onaylı bir izin
 *    o beyandaki İZİN VERİLEBİLİR her ERROR'u temizler — sonradan organizatör kuralı
 *    sıkılaştırdığı için ortaya çıkanı bile.
 * 2. İSTEMEK ALMAK DEĞİL. Bekleyen izin hiçbir şeyi değiştirmez: proje kilitli kalır
 *    ve `assign-system` talep karara bağlanana kadar KABUL ETMEZ (sıraya kaynak yapma).
 * 3. İZİN TEK BİR BEYANA AİTTİR. BAŞARILI HER /declare — kural çiğnemeyen temiz bir
 *    beyan bile — o projedeki canlı izinleri `durum 4`e (Geçersiz) düşürür. 4,50 m
 *    için verilmiş izin 12,00 m'lik yeni bir beyanı sessizce kapsamamalı.
 * 4. BAZI İHLALLER İÇİN İZİN VERİLEMEZ. Bunlar bayrak ne olursa olsun reddedilir ve
 *    izin satırı YAZILMAZ. Bugün tek örnek `AREA_REQUIRED` (boş alan; ortada
 *    izin verilecek bir ihlal yok) — ama listeyi sabitlemeyin, her ihlaldeki
 *    `permittable` alanını okuyun.
 *
 * ⚠️ YETKİ ZORLANMIYOR. `ProjeOzelIzinService.canDecide` bugün sadece "oturum var mı"
 *    diye bakıyor; teknik olarak talebi açan katılımcı kendi iznini onaylayabilir.
 *    Onayla/Reddet düğmelerini KATILIMCI EKRANINA KOYMAYIN — backend engellemiyor.
 *
 * ⚠️ `person_single_permit` ile KARIŞTIRMAYIN. UI'da o da "Özel İzin" diye geçiyor
 *    ama o bir KİŞİYİ belgeleri olmadan kapıdan geçiriyor; bu bir PROJEYİ fuar
 *    kurallarına rağmen beyan ettiriyor. Ayrı tablo, ayrı yaşam döngüsü, ayrı ekran.
 */

/** Not alanları backend'de SESSİZCE kırpılıyor — formda sınırı biz uygulayalım. */
export const OZEL_IZIN_NOT_MAX = 1000;

/**
 * ProjeOzelIzinConstant. Sadece 1 ve 2 CANLI; 3 ve 4 tarihtir — gösterilir,
 * hiçbir şeyi açmaz. DTO zaten `pending`/`approved` taşıyor, tam sayı karşılaştırmayın.
 */
export const OZEL_IZIN_DURUM = {
  1: { get label() { return i18n.t("services:projectService.onayBekliyor"); },                 color: 'warning',   live: true  },
  2: { get label() { return i18n.t("services:projectService.onaylandi"); },                     color: 'success',   live: true  },
  3: { get label() { return i18n.t("services:projectService.reddedildi"); },                    color: 'danger',    live: false },
  4: { get label() { return i18n.t("services:projectService.gecersizYeniBeyanVerildi"); }, color: 'secondary', live: false },
};

/**
 * Bu projede istenmiş TÜM izinler, YENİDEN ESKİYE. Reddedilen ve geçersizleşenler
 * dahil ("martta reddedildi, nisanda onaylandı" birinin soracağı bir soru).
 * ⚠️ CANLI izin zaten her ProjeBeyaniDTO'da `ozelIzin` olarak var — bu uç sadece
 *    GEÇMİŞ içindir, sayfa açılışında çağırmayın.
 * GET /projects/{id}/ozel-izin
 */
export async function getOzelIzinGecmisi(id, { forceLive = false } = {}) {
  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();
    return _mockOzelIzinler.filter((i) => i.projectId === id)
      .sort((a, b) => (a.id < b.id ? 1 : -1));
  }
  try {
    const res = await axios.get(`${BASE}/${id}/ozel-izin`, { headers: getAuthHeader() });
    return Array.isArray(res.data) ? res.data : [];
  } catch (e) { parseError(e); }
}

/**
 * ZATEN KAYITLI bir beyan için izin iste. Normal yol /declare üzerindeki bayraktır;
 * bu uç onun kapsayamadığı durum içindir: kabul edildiğinde YASAL olan bir beyanın
 * organizatör tavanı düşürdüğü/tipi yasakladığı için SONRADAN yasadışı olması.
 * Katılımcı bundan yeniden beyan vererek çıkamaz (sayılar zaten mutabık kalınmıştı)
 * ve zaten adım 3'ten sonra /declare ona kapalı.
 *
 * ⚠️ BAŞARISIZLIK ÇOĞUNLUKLA HTTP 200. "zaten bekleyen talep var" / "zaten geçerli
 *    izni var" 200 + result:false ile geliyor (api-endpoints.md burada YANLIŞ, 400
 *    diyor). Gerçek 400'ler de var: beyan yok · ortada izin verilecek ihlal yok ·
 *    izin verilemez bir ihlal ({message, violations[]} JSON'ı ile).
 * ⚠️ Not anahtarı burada `note` — /declare'deki `ozelIzinNotu` DEĞİL.
 * POST /projects/{id}/ozel-izin
 */
export async function requestOzelIzin(id, note = null, { forceLive = false } = {}) {
  const clean = note == null ? null : String(note).trim().slice(0, OZEL_IZIN_NOT_MAX) || null;
  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();
    const p = _mockProjects.find((x) => x.id === id);
    if (!p) throw new Error('Proje bulunamadı');
    if (_mockOzelIzinler.some((i) => i.projectId === id && (i.durum === 1 || i.durum === 2))) {
      throw new Error('Bu proje için zaten onay bekleyen ya da geçerli bir özel izin var.');
    }
    const row = _mockNewOzelIzin(p, clean);
    _mockOzelIzinler.unshift(row);
    p.projeBeyani = { ...(p.projeBeyani || {}), ozelIzin: row, ozelIzinBekliyor: true, ozelIzinTalepEdilebilir: false, locked: true };
    return row;
  }
  try {
    const res = await axios.post(`${BASE}/${id}/ozel-izin`, { note: clean }, { headers: getAuthHeader() });
    // GenericResultJson: 200 + result:false bir REDDİR, başarı değil.
    if (res.data && res.data.result === false) {
      const err = new Error(res.data.message || i18n.t("services:projectService.ozelIzinTalebiAcilamadi"));
      err.data = res.data;
      throw err;
    }
    return res.data?.data ?? res.data;
  } catch (e) { if (e.response) parseError(e); throw e; }
}

/**
 * Denetmen tepsisi: bir fuardaki KARARA BAĞLANMAMIŞ talepler, EN UZUN BEKLEYEN ÜSTTE.
 * Liste değil KUYRUK — pazartesiden beri bekleyen en üstte ve o projede hiçbir şey
 * kımıldayamaz.
 * ⚠️ `fuarId` ZORUNLU (@RequestParam int) — göndermezseniz Spring 400'ü kodumuz
 *    çalışmadan döner. Bilinmeyen fuar temiz bir [] verir, 404 değil.
 * ⚠️ Satırlar KOD taşır, MESAJ değil — cümleleri görmek için projeyi çözmek gerekir.
 * GET /projects/ozel-izin/bekleyenler?fuarId=
 */
export async function getBekleyenOzelIzinler(fuarId, { forceLive = false } = {}) {
  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();
    return _mockOzelIzinler
      .filter((i) => i.durum === 1 && Number(i.fuarId) === Number(fuarId))
      .sort((a, b) => (a.talepTarihi > b.talepTarihi ? 1 : -1));
  }
  try {
    const res = await axios.get(`${BASE}/ozel-izin/bekleyenler`, {
      params: { fuarId: Number(fuarId) }, headers: getAuthHeader(),
    });
    return Array.isArray(res.data) ? res.data : [];
  } catch (e) { parseError(e); }
}

/**
 * Karar. `onay` GERÇEK bir JSON boolean olmalı.
 * ⚠️ Gevşek okunuyor: true/"true"/1 onaylar — ama BAŞKA HER STRING false okunur,
 *    yani "evet" / "yes" / "onay" göndermek izni SESSİZCE REDDEDER. Yokluk/null tek
 *    400 veren durumdur.
 * ⚠️ REDDEDERKEN GEREKÇE ZORUNLU. "Hayır" demenin gerekçesiz hâli katılımcıya
 *    üzerinde çalışacak hiçbir şey bırakmıyor ve `kararNotu` bunu okuyacağı tek yer.
 * ⚠️ Eşzamanlı karar koruması gerçek: UPDATE `AND durum = 1` taşıyor, ikinci denetmen
 *    "başkası karara bağladı, sayfayı yenileyin" alır — 200 + result:false ile.
 * PATCH /projects/ozel-izin/{izinId}/karar
 */
export async function decideOzelIzin(izinId, onay, note = null, { forceLive = false } = {}) {
  const approve = onay === true;
  const clean = note == null ? null : String(note).trim().slice(0, OZEL_IZIN_NOT_MAX) || null;
  if (!approve && !clean) throw new Error(i18n.t("services:projectService.reddederkenGerekceYazilmalidir"));
  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();
    const row = _mockOzelIzinler.find((i) => i.id === Number(izinId));
    if (!row) throw new Error(`Özel izin bulunamadı: ${izinId}`);
    if (row.durum !== 1) throw new Error(`Bu özel izin zaten karara bağlanmış: ${OZEL_IZIN_DURUM[row.durum]?.label}.`);
    row.durum = approve ? 2 : 3;
    row.durumName = OZEL_IZIN_DURUM[row.durum].label;
    row.pending = false;
    row.approved = approve;
    row.kararVeren = 1; row.kararVerenAdi = 'FDS Uzmanı';
    row.kararTarihi = new Date().toISOString(); row.kararNotu = clean;
    const p = _mockProjects.find((x) => x.id === row.projectId);
    if (p?.projeBeyani) {
      p.projeBeyani.ozelIzin = approve ? row : null;
      p.projeBeyani.ozelIzinBekliyor = false;
      p.projeBeyani.ozelIzinli = approve;
      p.projeBeyani.locked = !approve;
      // Onaylı izin ihlalleri LİSTEDEN SİLMEZ, severity'lerini PERMITTED yapar.
      p.projeBeyani.violations = (p.projeBeyani.violations || []).map((v) =>
        approve && v.severity === 'ERROR' && v.permittable !== false
          ? { ...v, severity: 'PERMITTED', ozelIzinId: row.id }
          : v);
    }
    return row;
  }
  try {
    const res = await axios.patch(`${BASE}/ozel-izin/${izinId}/karar`,
      { onay: approve, note: clean }, { headers: getAuthHeader() });
    if (res.data && res.data.result === false) {
      const err = new Error(res.data.message || i18n.t("services:projectService.kararKaydedilemedi"));
      err.data = res.data;
      throw err;
    }
    return res.data?.data ?? res.data;
  } catch (e) { if (e.response) parseError(e); throw e; }
}

// ═════════════════════════════════════════════════════════════════════════════
// 13. DUVAR BİLGİSİ (project.walls[]) — 2026-08-25
//     Kaynak: docs/guides/FE-ProjectController-declaration-forms-guide.md §7
// ═════════════════════════════════════════════════════════════════════════════
/**
 * Katılımcının kendi duvar listesi. SAF DEPOLAMA — hiçbir şey yargılanmıyor: tavan
 * kontrolü yok, alan kontrolü yok, kroki karşılaştırması yok, hiçbir zorunlu belge
 * veya yükseklik bandı bunun yüzünden kımıldamıyor.
 *
 * Sorduğu soru şu: stand gerçekten organizatöre söz verilen alan mı? Katılımcı m²
 * için ödüyor ve sessizce büyüyen stand ödenmemiş zemin demek. Duvar GENİŞLİKLERİ
 * iddiadır; bizim kendi krokimizle karşılaştırılmak üzere duruyor — o karşılaştırma
 * REZERVE, yazılmadı (kroki motoru yeniden yazılıyor).
 *
 * ⚠️ Liste TOPTAN DEĞİŞİR, eklenmez: yeniden göndermek "işte standım" demektir,
 *    "bir duvar daha" değil. `index` bizim atadığımız 1..n ETİKETTİR, kimlik değil.
 * ⚠️ GERİ ALINAMAZ. En az bir duvar zorunlu, `{wallCount:0, walls:[]}` 400 —
 *    yani gönderilmiş bir listeyi API üzerinden temizlemenin yolu yok.
 * ⚠️ `wallCount` submit anında walls.length'ten hesaplanmalı, formda ayrı bir sayaç
 *    olarak TUTULMAMALI: uyuşmazlık 400 döner ve kullanıcıya backend hatası gibi okunur.
 * ⚠️ GENİŞLİKLERDEN m² HESAPLAMAYIN. Aynı altı genişlikle L şeklindeki bir stand
 *    farklı alan çevreler. Backend de yapmıyor.
 * POST /projects/{id}/declaration/walls
 */
export const WALLS_MAX = 50;

export async function saveDeclarationWalls(id, walls, { forceLive = false, ozelIzinTalep = false, ozelIzinNotu = null } = {}) {
  const rows = (walls || [])
    .map((w) => ({
      width: w.width === '' || w.width == null ? null : Number(w.width),
      height: w.height === '' || w.height == null ? null : Number(w.height),
      // #676: bir duvar çizimdeki hangi kenar olduğunu (şekil + kenar) ve — doluysa — açıklığını (metre) taşır;
      // backend #675 kuralını yalnızca kenarını söyleyen duvarlar için işletir.
      ...(w.edgeIndex != null ? { edgeIndex: Number(w.edgeIndex) } : {}),
      ...(w.shapeId != null ? { shapeId: Number(w.shapeId) } : {}),
      ...(w.aciklik === '' || w.aciklik == null ? {} : { aciklik: Number(w.aciklik) }),
    }))
    .filter((w) => w.width != null || w.height != null);

  if (rows.length === 0) throw new Error(i18n.t("services:projectService.enAzBirDuvarBilgisi"));
  if (rows.length > WALLS_MAX) throw new Error(i18n.t("services:projectService.birStandIcinEnFazla", { WALLS_MAX }));
  rows.forEach((w, i) => {
    if (!(w.width > 0)) throw new Error(i18n.t("services:projectService.duvarinGenisligi0DanBuyuk", { v: i + 1 }));
    if (!(w.height > 0)) throw new Error(i18n.t("services:projectService.duvarinYuksekligi0DanBuyuk", { v: i + 1 }));
  });

  // #678: duvar yükseklikleri artık yargılanıyor; kural ihlali olan liste reddedilir (400 JSON { message, violations[] })
  // ve izin verilebilir ihlallerde yalnızca ozelIzinTalep ile kaydedilip bir Proje Yardım Merkezi talebi açılır.
  const body = {
    wallCount: rows.length, walls: rows,
    ...(ozelIzinTalep ? { ozelIzinTalep: true, ozelIzinNotu: ozelIzinNotu ?? null } : {}),
  };

  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();
    const p = _mockProjects.find((x) => x.id === id);
    if (!p) throw new Error('Proje bulunamadı');
    const now = new Date().toISOString();
    p.projeBeyani = {
      ...(p.projeBeyani || {}),
      walls: rows.map((w, i) => ({ index: i + 1, ...w })),
      wallCount: rows.length,
      wallsDeclared: true, wallsDeclaredAt: now, wallsDeclaredBy: 1,
      wallsAccepted: false, wallsAcceptedAt: null, wallsAcceptedBy: null,
    };
    return p.projeBeyani;
  }
  try {
    // Yanıt TÜM ProjeBeyaniDTO'dur — violations/locked/ozelIzin her okumada yeniden
    // türetildiği için sayfayı bu yanıttan komple yeniden çizin.
    const res = await axios.post(`${BASE}/${id}/declaration/walls`, body, { headers: getAuthHeader() });
    return res.data;
  } catch (e) { parseError(e); }
}

/**
 * #676: standın ÇİZİLMİŞ duvarları — Proje Beyanı sihirbazının Duvarlar adımı çizimdeki her kenar için bir satır
 * gösterir. Kaynak fdsVector'ın her kayıtta FDS-Backend'e ittiği kenarlar (stand_vector_wall); fdsVector'a çağrı yok.
 * Yanıt:
 *   drawn            — standın en az bir çizilmiş duvarı var mı (false: çizilmemiş ya da plan V137'den beri kaydedilmemiş)
 *   walls[]          — { shapeId, shapeNo, edgeIndex, lengthM, heightM (null = çizilmemiş), facesCorridor }
 *   shapeCount       — stand kaç şekil olarak çizilmiş (her şeklin kendi 0. kenarı var)
 *   declaredHMax     — standın beyan ettiği H Max (beyan öncesi null): çizilmemiş yüksekliğin başlangıç değeri
 *   minAciklikYuzde  — fuarın asgari açıklık yüzdesi; null = kural kapalı, açıklık isteğe bağlı
 * ⚠️ Kenarın UZUNLUĞU var, köşe koordinatı yok: şeklin kendisi bu satırlardan yeniden çizilemez.
 * GET /projects/{id}/drawn-walls
 */
export async function getDrawnWalls(id, { forceLive = false } = {}) {
  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();
    return { drawn: false, standId: null, fuarId: null, shapeCount: 0, declaredHMax: null, minAciklikYuzde: null, walls: [] };
  }
  try {
    const res = await axios.get(`${BASE}/${id}/drawn-walls`, { headers: getAuthHeader() });
    return res.data;
  } catch (e) { parseError(e); }
}

// ═════════════════════════════════════════════════════════════════════════════
// 14. FUARA ÖZEL BEYAN FORMLARI (fuar_proje_beyani_form) — 2026-08-28
//     Kaynak: docs/guides/FE-ProjectController-declaration-forms-guide.md
// ═════════════════════════════════════════════════════════════════════════════
/**
 * Organizatörün KENDİ fuarının Proje Beyanı'na taktığı ek formlar. Bu tablo SORU DA
 * SAKLAMAZ CEVAP DA — iki form motoru zaten vardı ve verileri yerinde kalıyor. Ek
 * satırın taşıdığı tek şey üç gerçek: HANGİ FUAR, HANGİ SIRA, ZORUNLU MU.
 *
 * ⚠️ `zorunlu` BEYANI değil KABULÜ engeller: doldurulmamış zorunlu form
 *    `assign-system`'i 400'ler, `POST /declare`'i etkilemez. Fuarın ek formları
 *    standart beyandan SONRA gelir; önce istemek, katılımcının stand tipini
 *    söyleyebilmek için o stand hakkında bir ankete cevap vermesi demek olurdu.
 * ⚠️ `formTipi: 1` (Sürdürülebilirlik) → cevaplar PROJE BELGESİNE gömülü, ayrı bir
 *    id yok, `submissionId` her zaman null. `formTipi: 2` (Serbest Form) → ayrı bir
 *    `free_form_submissions` belgesi, `_id`si `submissionId` olarak geliyor.
 * ⚠️ Beyana YALNIZCA `STAND` hedefli serbest formlar takılabilir — seçiciyi kendiniz
 *    süzün, backend'in reddi 200 + result:false ile gelir.
 */
export const BEYAN_FORM_TIPI = {
  1: { get label() { return i18n.t("services:projectService.surdurulebilirlikFormu"); }, color: 'success' },
  2: { get label() { return i18n.t("services:projectService.serbestForm"); },            color: 'info'    },
  // Backlog #92/#93: bilgilendirme amaçlıdır — eklendiği an her proje için "dolu" sayılır,
  // zorunlu işaretlense bile hiçbir beyanı kilitlemez (bkz. ProjeBeyaniFormService#applyCompletion).
  3: { label: 'PDF',                     color: 'secondary' },
};

/**
 * Organizatör grid'i: bir fuara takılmış her şey.
 * `includeInactive` ÇIKARILMIŞ satırları da getirir — geri eklemenin tek yolu onları
 * görmek. Sıralama `sira ASC, id ASC`.
 * GET /projects/declaration-forms?fuarId=&includeInactive=
 */
export async function getFuarDeclarationForms(fuarId, { includeInactive = false, forceLive = false } = {}) {
  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();
    return (_mockBeyanFormlari[fuarId] ?? []).filter((f) => includeInactive || f.aktif);
  }
  try {
    const res = await axios.get(`${BASE}/declaration-forms`, {
      params: { fuarId: Number(fuarId), includeInactive },
      headers: getAuthHeader(),
    });
    return Array.isArray(res.data) ? res.data : [];
  } catch (e) { parseError(e); }
}

/**
 * Takma — ya da ÇIKARILMIŞ birini geri getirme. Arkasında
 * `INSERT … ON DUPLICATE KEY UPDATE` var, yani "zaten ekli" diye bir hata YOK.
 * ⚠️ Yeniden POST bir "geri yükleme" DEĞİL: `zorunlu`/`sira`'yı gönderdiğinizle
 *    EZER ve eksik `zorunlu` false'a düşer — zorunlu bir formu sessizce
 *    isteğe bağlı yapar. "Olduğu gibi geri getir" istiyorsanız updateDeclarationForm
 *    ile sadece `{aktif:true}` gönderin.
 * ⚠️ Doğrulama hataları 200 + result:false — `response.ok`'a asla bakmayın.
 * POST /projects/declaration-forms
 */
export async function attachDeclarationForm(body, { forceLive = false } = {}) {
  const tip = Number(body.formTipi);
  const payload = {
    fuarId: Number(body.fuarId),
    formTipi: tip,
    // formTipi 1 için backend zaten "" yapıyor; gönderdiğimizi yok sayıyor. formTipi 2 şablon
    // id'si, 3 (PDF) uploadDeclarationFormPdf'in döndürdüğü dosya adı — ikisi de formRef.
    formRef: tip === 2 || tip === 3 ? (body.formRef ?? '') : '',
    zorunlu: Boolean(body.zorunlu),
    sira: body.sira === '' || body.sira == null ? 0 : Number(body.sira),
  };
  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();
    const list = _mockBeyanFormlari[payload.fuarId] ?? (_mockBeyanFormlari[payload.fuarId] = []);
    const existing = list.find((f) => f.formTipi === payload.formTipi && (f.formRef ?? '') === payload.formRef);
    if (existing) Object.assign(existing, payload, { aktif: true });
    else list.push(_mockNewBeyanForm(payload));
    return list;
  }
  try {
    const res = await axios.post(`${BASE}/declaration-forms`, payload, { headers: getAuthHeader() });
    if (res.data && res.data.result === false) throw new Error(res.data.message || i18n.t("services:projectService.formEklenemedi"));
    return res.data?.data ?? res.data;
  } catch (e) { if (e.response) parseError(e); throw e; }
}

/**
 * PDF-tipi (formTipi 3) bir Ek Form eki için dosyayı yükler — eklenmemiş, tek başına. Yükleme
 * eklemeden AYRI (fds-architecture): dönen `fileName`'i `formRef` olarak attachDeclarationForm'a
 * gönderin. POST /projects/declaration-forms/pdf
 */
export async function uploadDeclarationFormPdf(file, { forceLive = false } = {}) {
  if (!file) throw new Error(i18n.t("services:projectService.pdfDosyasiSecilmedi"));
  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();
    const fileName = `${Date.now()}_mock.pdf`;
    return { fileName, formUrl: `/mock/${fileName}` };
  }
  const form = new FormData();
  form.append('file', file);
  try {
    const res = await axios.post(`${BASE}/declaration-forms/pdf`, form, {
      headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' },
    });
    if (res.data && res.data.result === false) throw new Error(res.data.message || i18n.t("services:projectService.pdfYuklenemedi"));
    return res.data?.data ?? res.data;
  } catch (e) { if (e.response) parseError(e); throw e; }
}

/**
 * ⚠️ ADI PUT AMA DAVRANIŞI PATCH — bu API'de tek. Fuar kural setinin (/kural) aksine
 *    GÖNDERİLMEYEN anahtar OLDUĞU GİBİ KALIR, varsayılana dönmez. Nedeni kaynakta
 *    yazılı: bu ekran bir seferde tek bayrak değiştiriyor, eksik alanı varsaymak
 *    birinin zorunlu yaptığı formu sessizce isteğe bağlı yapardı.
 *    Alışkanlıkla tüm form nesnesini göndermeyin — sadece değişen bayrağı.
 * PUT /projects/declaration-forms/{id}
 */
export async function updateDeclarationForm(id, patch, { forceLive = false } = {}) {
  const payload = {};
  if (patch.zorunlu !== undefined) payload.zorunlu = Boolean(patch.zorunlu);
  if (patch.sira !== undefined) payload.sira = Number(patch.sira);
  if (patch.aktif !== undefined) payload.aktif = Boolean(patch.aktif);
  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();
    let found = null;
    Object.values(_mockBeyanFormlari).forEach((list) => {
      const row = list.find((f) => f.id === Number(id));
      if (row) { Object.assign(row, payload); found = row; }
    });
    if (!found) throw new Error(`Beyan formu eki bulunamadı: ${id}`);
    return found;
  }
  try {
    const res = await axios.put(`${BASE}/declaration-forms/${id}`, payload, { headers: getAuthHeader() });
    if (res.data && res.data.result === false) throw new Error(res.data.message || i18n.t("services:projectService.guncellenemedi"));
    return res.data?.data ?? res.data;
  } catch (e) { if (e.response) parseError(e); throw e; }
}

/**
 * YUMUŞAK çıkarma — `aktif = 0`. Satır kalır, hiçbir şeyi engellemez ve
 * `includeInactive` dışındaki her okumadan kaybolur. SERT silme YOK.
 * ⚠️ "Bu kalıcı, emin misiniz?" diye sormayın: kalıcı değil, geri alması tek tık.
 * DELETE /projects/declaration-forms/{id}
 */
export async function detachDeclarationForm(id, { forceLive = false } = {}) {
  if (USE_MOCK_DATA && !forceLive) return updateDeclarationForm(id, { aktif: false });
  try {
    const res = await axios.delete(`${BASE}/declaration-forms/${id}`, { headers: getAuthHeader() });
    if (res.data && res.data.result === false) throw new Error(res.data.message || i18n.t("services:projectService.cikarilamadi"));
    return res.data?.data ?? res.data;
  } catch (e) { if (e.response) parseError(e); throw e; }
}

// ─── "Tüm Beyanlara Uygula" (backlog #94/#95) ────────────────────────────────
// Yeni bir zorunlu form eklendiğinde, o formu henüz karşılamayan ZATEN KABUL EDİLMİŞ beyanları
// sistem atamasını GERİ ALARAK yeniden "beklemede" yapar — declareProjeBeyani'nin sıfırdan bir
// beyanda ve applyProposal'ın kabul edilen bir değişiklik isteğinde yaptığı AYNI şey. Geri
// alınabilir: her etkilenen proje POST /system-assignment-history/{id}/restore ile tek tek
// geri yüklenebilir (o projeye o andan sonra yeniden atama yapılmadıysa).

/**
 * Salt-okunur önizleme: fuarId'de formId'yi henüz karşılamayan, hâlâ kabul edilmiş beyanlar —
 * onay modalının "kaç kayıt etkilenecek" sorusunun cevabı. GET /projects/declaration-forms/{formId}/impact
 */
export async function previewDeclarationFormImpact(formId, fuarId, { forceLive = false } = {}) {
  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();
    return { count: 0, projects: [] }; // mock modda "zaten kabul edilmiş beyan" kavramı yok
  }
  try {
    const res = await axios.get(`${BASE}/declaration-forms/${formId}/impact`, {
      params: { fuarId: Number(fuarId) }, headers: getAuthHeader(),
    });
    return res.data ?? { count: 0, projects: [] };
  } catch (e) { parseError(e); }
}

/**
 * Uygular: etkilenen her beyanın sistem ataması geri alınır, her biri için bir geçmiş kaydı
 * yazılır. Sessiz bir işlem değildir — çağırmadan önce kullanıcıya sayıyla onaylatın.
 * POST /projects/declaration-forms/{formId}/apply-to-accepted
 */
export async function applyDeclarationFormToAccepted(formId, fuarId, sebep, { forceLive = false } = {}) {
  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();
    return { message: '0 kabul edilmiş beyan geri alındı.', data: [] };
  }
  try {
    const res = await axios.post(`${BASE}/declaration-forms/${formId}/apply-to-accepted`,
      { fuarId: Number(fuarId), sebep: sebep || undefined }, { headers: getAuthHeader() });
    if (res.data && res.data.result === false) throw new Error(res.data.message || i18n.t("services:projectService.uygulanamadi"));
    return res.data ?? {};
  } catch (e) { if (e.response) parseError(e); throw e; }
}

/** Geri alınan tek bir beyanı geri yükler. POST /projects/system-assignment-history/{id}/restore */
export async function restoreSystemAssignment(historyId, { forceLive = false } = {}) {
  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();
    return { message: 'Beyan kabulü geri yüklendi.' };
  }
  try {
    const res = await axios.post(`${BASE}/system-assignment-history/${historyId}/restore`, {},
      { headers: getAuthHeader() });
    if (res.data && res.data.result === false) throw new Error(res.data.message || i18n.t("services:projectService.geriYuklenemedi"));
    return res.data ?? {};
  } catch (e) { if (e.response) parseError(e); throw e; }
}

/**
 * KATILIMCININ KONTROL LİSTESİ — fuarın AKTİF formları, her biri BU PROJE için
 * doldurulmuş mu bilgisiyle. `assign-system`'in tam olarak reddettiği şey budur.
 *
 * ⚠️ `completionKnown` OKUNMADAN `filled`'a güvenmeyin: fuar seviyesindeki listede
 *    `filled` her zaman false'tur çünkü "hangi proje" diye kimse sormamıştır — bu
 *    "henüz doldurulmadı" ile AYNI ŞEY DEĞİL.
 * ⚠️ `formMissing: true` satırları GİZLEMEYİN, KIRIK gösterin. Dangling bir ZORUNLU
 *    form o fuardaki HER beyanı kabul edilemez yapar ve bunu kimse açamaz — bu
 *    kasıtlı gürültü, birinin düzeltmesi gereken bir konfigürasyon hatası.
 * GET /projects/{id}/declaration-forms
 */
export async function getProjectDeclarationForms(id, { forceLive = false } = {}) {
  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();
    const p = _mockProjects.find((x) => x.id === id);
    const list = (_mockBeyanFormlari[p?.fuarId] ?? []).filter((f) => f.aktif);
    return list.map((f) => ({
      ...f,
      completionKnown: true,
      // PDF (#92): bilgilendirme amaçlıdır, eklendiği an her proje için dolu sayılır.
      filled: f.formTipi === 3 ? true : Boolean(_mockFormDoldurma[`${id}:${f.id}`]),
      filledAt: f.formTipi === 3 ? f.updateDate : (_mockFormDoldurma[`${id}:${f.id}`] ?? null),
      submissionId: null,
    }));
  }
  try {
    const res = await axios.get(`${BASE}/${id}/declaration-forms`, { headers: getAuthHeader() });
    return Array.isArray(res.data) ? res.data : [];
  } catch (e) { parseError(e); }
}

/**
 * "Kabul et" düğmesini kilitleyen formlar. Tek doğru test bu: `zorunlu && !filled`,
 * ve `completionKnown` false ise hiçbir şey söyleyemeyiz (fuar seviyesindeki liste).
 */
export const blockingDeclarationForms = (forms) =>
  (forms || []).filter((f) => f.zorunlu && f.completionKnown && !f.filled);

export const FILE_APPROVAL_STATUS = {
  0: { get label() { return i18n.t("services:projectService.beklemede"); },  color: 'warning' },
  1: { get label() { return i18n.t("services:projectService.onaylandi"); },  color: 'success' },
  2: { get label() { return i18n.t("services:projectService.reddedildi"); }, color: 'danger'  },
};

// ESKİ iki yönlü ayrım — sadece geriye dönük uyumluluk için duruyor. Yeni UI
// REVISION_TARGET_KIND göstersin: "Görsel" bir belge revizyonu, "Yapısal" ise hem
// beyan alanı hem serbest not olabilir, yani tek başına ne olduğunu söylemiyor.
export const REVISION_TYPE = {
  structural: { get label() { return i18n.t("services:projectService.yapisal"); }, color: 'info'   },
  visual:     { get label() { return i18n.t("services:projectService.gorsel"); },  color: 'primary' },
};

// ⚠️ ÜÇ durum. `status === 'open'` ile "bitmemiş" testi yapan her yer YANLIŞ —
// 'answered' da bitmemiştir ve üstelik sıra BİZDEDİR (bkz. bölüm 9 başlığı).
export const REVISION_STATUS = {
  open:     { get label() { return i18n.t("services:projectService.acik"); },       color: 'danger'  },
  answered: { get label() { return i18n.t("services:projectService.yanitlandi"); }, color: 'warning' },
  resolved: { get label() { return i18n.t("services:projectService.cozuldu"); },    color: 'success' },
};

// Bir revizyonun NEYLE ilgili olduğu — yeni kod tipe değil buna baksın.
export const REVISION_TARGET_KIND = {
  DOCUMENT:   { get label() { return i18n.t("services:projectService.belge"); },       color: 'primary'   },
  SPEC_FIELD: { get label() { return i18n.t("services:projectService.beyanAlani"); }, color: 'info'      },
  FREEFORM:   { get label() { return i18n.t("services:projectService.serbestNot"); }, color: 'secondary' },
};

// Kimin açtığı — rozet metni "kim bekliyor"u okunur kılmak için.
export const REVISION_OPENED_BY = {
  STAFF:   { get label() { return i18n.t("services:projectService.fdsTalebi"); },          color: 'dark'      },
  COMPANY: { get label() { return i18n.t("services:projectService.dekoratorTalebi"); },    color: 'secondary' },
};

// Kapanışta ne yaptığımız (sadece /accept yazar; status=resolved ile kapatılan bir
// revizyonda outcome null kalır — "kararı kayda geçmeden kapatıldı" demektir).
export const REVISION_OUTCOME = {
  APPLIED:           { get label() { return i18n.t("services:projectService.uygulandi"); },              color: 'success'   },
  APPLIED_WITH_EDIT: { get label() { return i18n.t("services:projectService.duzenlenerekUygulandi"); }, color: 'info'      },
  ACKNOWLEDGED:      { get label() { return i18n.t("services:projectService.kabulYazilmadi"); },      color: 'secondary' },
};

// Bir revizyon "bitmemiş" mi? Tek doğru test bu — üç durumlu status'ün ardından
// `=== 'open'` kontrolleri sessizce ANSWERED'ları düşürüyordu.
export const isRevisionUnfinished = (r) => r?.status !== 'resolved';
// Sıra BİZDE mi? (tampon dolu, karar bekliyor)
export const isRevisionAwaitingUs = (r) => r?.status === 'answered';

// ─── Proje Beyanı — kabul edilen yükseklik (2026-08-24 yön değişikliği) ───────
// `promisedHeightNumber` HÂLÂ tel üzerinde ama artık YANLIŞ İSİMLİ: eskiden
// "önceden mutabık kalınan yükseklik"ti, şimdi "kabul ettiğimiz yükseklik" —
// aynı sayı, farklı anlam. Yeni ad `acceptedHeightNumber`. İkisi de gelene kadar
// tek okuma noktası burası olsun ki sayfalarda ?? zincirleri çoğalmasın.
export const getAcceptedHeight = (pb) => pb?.acceptedHeightNumber ?? pb?.promisedHeightNumber ?? null;

// Adım 3 (assign-system) çalıştı mı?
// ⚠️ `pb.accepted`'a BAKMAYIN. Canlıda (stand 57557, 2026-08-25) `accepted: true` ile
//    `systemAssigned: false` birlikte geliyor — `accepted` gerçekte "projenin bir
//    yüksekliği var mı" demek (eski `promisedMissing`'in tersi), "kabul yazımı oldu mu"
//    değil. Eski akışta oluşturulmuş kayıtlarda yükseklik zaten adım 1'de girildiği için
//    `accepted` daha ilk günden true. Adım 3'ün çalıştığının tek doğru kanıtı atamanın
//    kendisi.
export const isDeclarationAccepted = (pb) =>
  pb?.systemAssigned === true || (pb?.declared === true && pb?.awaitingSystemAssignment === false);

// Canlı modda uploadedFiles[].storedFileName'den gerçek dosya URL'i — permitAll,
// header gerekmez (bkz. FE-ProjectController-project-guide.md "Files: uploading
// and displaying"). _normalizeLiveRevision'daki aynı satırla aynı desen.
export function getProjectFileUrl(storedFileName) {
  if (!storedFileName) return null;
  return `${JAVA_BACKEND_API_URL.replace(/\/api\/v1$/, '')}/public/files/project_files/${storedFileName}`;
}

